import React from 'react';

/* ── Ortak yardımcı bileşenler ────────────────────────────────────────────── */

const Gorsel: React.FC<{ aciklama: string }> = ({ aciklama }) => (
    <div style={{
        background: '#f8fafc', border: '2px dashed #cbd5e1', borderRadius: 10,
        padding: '22px 16px', margin: '16px 0', textAlign: 'center', color: '#94a3b8'
    }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>🖼️</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>GÖRSEL</div>
        <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{aciklama}</div>
    </div>
);

const Adim: React.FC<{ no: number; baslik: string; children: React.ReactNode }> = ({ no, baslik, children }) => (
    <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
        <div style={{
            flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
            background: '#cd6906', color: '#fff', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700
        }}>{no}</div>
        <div style={{ flex: 1, paddingTop: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 6 }}>{baslik}</div>
            <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.65 }}>{children}</div>
        </div>
    </div>
);

const Ipucu: React.FC<{ tur?: 'bilgi' | 'uyari' | 'basari'; children: React.ReactNode }> = ({ tur = 'bilgi', children }) => {
    const renkler = {
        bilgi:  { bg: '#eff6ff', border: '#bfdbfe', icon: 'ℹ️', renk: '#1e40af' },
        uyari:  { bg: '#fffbeb', border: '#fde68a', icon: '⚠️', renk: '#92400e' },
        basari: { bg: '#f0fdf4', border: '#bbf7d0', icon: '✅', renk: '#166534' },
    };
    const r = renkler[tur];
    return (
        <div style={{ background: r.bg, border: `1px solid ${r.border}`, borderRadius: 9, padding: '12px 16px', margin: '14px 0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{r.icon}</span>
            <div style={{ fontSize: 13, color: r.renk, lineHeight: 1.6 }}>{children}</div>
        </div>
    );
};

const AlanTablosu: React.FC<{ alanlar: { ad: string; aciklama: string; zorunlu?: boolean }[] }> = ({ alanlar }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '12px 0' }}>
        <thead>
            <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '9px 14px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', width: '35%' }}>Alan</th>
                <th style={{ padding: '9px 14px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}>Açıklama</th>
            </tr>
        </thead>
        <tbody>
            {alanlar.map((a, i) => (
                <tr key={i} style={{ background: i % 2 === 1 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, color: '#111827' }}>
                        {a.ad}
                        {a.zorunlu && <span style={{ color: '#ef4444', marginLeft: 4, fontSize: 11 }}>*</span>}
                    </td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid #f3f4f6', color: '#4b5563' }}>{a.aciklama}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const BaslikH3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '28px 0 10px', paddingBottom: 8, borderBottom: '1.5px solid #f1f5f9' }}>{children}</h3>
);

/* ── Makale içerikleri ────────────────────────────────────────────────────── */

export const MAKALE_ICERIKLERI: Record<string, React.ReactNode> = {

    /* ════════════════════════════════════════════════════════════
       1. Yeni Proje Oluşturma
    ════════════════════════════════════════════════════════════ */
    'proje-yonetimi/yeni-proje-olusturma': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                YapiCRM'de her inşaat projesi ayrı bir proje kaydı altında yönetilir. Proje oluşturulduktan sonra altına blok, kat, daire ve diğer tanımlamalar eklenir.
            </p>

            <BaslikH3>Proje Oluşturma Adımları</BaslikH3>

            <Adim no={1} baslik="Ayarlar > Projeler sayfasına gidin">
                Üst menüden <strong>Ayarlar</strong>'a tıklayın. Açılan sayfada <strong>Proje Tanımlamaları</strong> sekmesi altındaki <strong>Projeler</strong> bölümüne gidin.
                Mevcut projeler bu sayfada listelenir.
            </Adim>

            <Gorsel aciklama="Ayarlar > Proje Tanımlamaları > Projeler sayfası — sağ üstte '+ Yeni Proje Ekle' butonu görünür" />

            <Adim no={2} baslik="'+ Yeni Proje Ekle' butonuna tıklayın">
                Sayfanın sağ üst köşesindeki turuncu <strong>+ Yeni Proje Ekle</strong> butonuna tıklayın. Proje oluşturma formu açılır.
            </Adim>

            <Adim no={3} baslik="Proje bilgilerini doldurun">
                Aşağıdaki alanları doldurun:
                <AlanTablosu alanlar={[
                    { ad: 'Proje Adı', aciklama: 'Projenin tam adı (örn. "Terrace Park Residence")', zorunlu: true },
                    { ad: 'Ada', aciklama: 'Tapu bilgisindeki ada numarası' },
                    { ad: 'Parsel', aciklama: 'Tapu bilgisindeki parsel numarası' },
                    { ad: 'Adres', aciklama: 'Projenin tam adresi' },
                    { ad: 'Şehir', aciklama: 'Projenin bulunduğu şehir' },
                    { ad: 'KDV (%)', aciklama: 'Satışlarda uygulanacak KDV oranı (örn. 1, 10, 20)', zorunlu: true },
                    { ad: 'Başlama Tarihi', aciklama: 'İnşaatın başladığı veya başlayacağı tarih' },
                    { ad: 'Bitiş Tarihi', aciklama: 'Planlanan teslim tarihi' },
                    { ad: 'Proje Durumu', aciklama: '"Planlanan", "Devam Ediyor" veya "Tamamlandı"', zorunlu: true },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni proje oluşturma formu ekran görüntüsü — tüm alanlar dolu hâlde" />

            <Adim no={4} baslik="Projeyi kaydedin">
                Formu doldurduktan sonra <strong>Kaydet</strong> butonuna tıklayın. Proje oluşturulur ve proje detay sayfasına yönlendirilirsiniz.
            </Adim>

            <Ipucu tur="basari">
                Proje kaydedildiğinde sistem otomatik olarak bir <strong>Takip No</strong> atar. Bu numara sözleşme ve ödemelerde projeye bağlantı için kullanılır.
            </Ipucu>

            <BaslikH3>Proje Logosu Ekleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Proje detay sayfasında <strong>Proje Logosu</strong> alanına tıklayarak logo yükleyebilirsiniz. Logo, sunumlarda ve dokümanlarda görünür.
                PNG veya JPG formatında, en fazla 5 MB boyutunda dosya yüklenebilir.
            </p>

            <Gorsel aciklama="Proje detay sayfası — logo yükleme alanı vurgulanmış hâlde" />

            <Ipucu tur="bilgi">
                Proje oluşturulduktan sonra yapmanız gereken bir sonraki adım <strong>Blok, Kat, Cephe ve Manzara tanımlamalarını</strong> yapmaktır.
                Bu tanımlamalar yapılmadan daire eklenemez.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       2. Kat, Blok, Cephe, Manzara Tanımlama
    ════════════════════════════════════════════════════════════ */
    'proje-yonetimi/kat-blok-cephe-manzara': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Daire eklemeden önce projenizin fiziksel yapısını tanımlamanız gerekir: bloklar, katlar, cepheler ve manzaralar.
                Bu tanımlamalar <strong>Ayarlar &rsaquo; Proje Tanımlamaları</strong> menüsünden yapılır.
            </p>

            <BaslikH3>Blok Tanımlama</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Blok, bir projede birden fazla bina veya kule varsa her birini ayrı ayrı tanımlamak için kullanılır.
                Tek bloklu projelerde genellikle "A Blok" veya projenin adı girilir.
            </p>

            <Adim no={1} baslik="Proje detay sayfasını açın">
                Projeler listesinden ilgili projeye tıklayın. Proje detay sayfasında <strong>Bloklar</strong> sekmesine geçin.
            </Adim>
            <Adim no={2} baslik="Yeni blok ekleyin">
                <strong>+ Blok Ekle</strong> butonuna tıklayın. Blok adını girin (örn. "A Blok", "B Blok") ve kaydedin.
            </Adim>

            <Gorsel aciklama="Proje detayı — Bloklar sekmesi, '+Blok Ekle' butonu ve blok listesi görünür hâlde" />

            <Ipucu tur="bilgi">
                Her blok için ayrı bir <strong>Takip No</strong> otomatik oluşturulur. Daire ekleme sırasında hangi bloğa ait olduğunu seçersiniz.
            </Ipucu>

            <BaslikH3>Kat Tanımlama</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Katlar <strong>Ayarlar &rsaquo; Proje Tanımlamaları &rsaquo; Katlar</strong> menüsünden tanımlanır ve tüm projeler için geçerlidir.
                Zemin kat, bodrum, normal katlar burada oluşturulur.
            </p>

            <Adim no={1} baslik="Katlar sayfasına gidin">
                Ayarlar menüsünden <strong>Proje Tanımlamaları &rsaquo; Katlar</strong> bölümünü açın.
            </Adim>
            <Adim no={2} baslik="Yeni kat ekleyin">
                <strong>+ Yeni Kat</strong> butonuna tıklayın. Kat adını (örn. "Zemin Kat", "1. Kat", "Bodrum") ve sıra numarasını girin.
                Sıra numarası katların doğru sıralanmasını sağlar.
            </Adim>

            <AlanTablosu alanlar={[
                { ad: 'Kat Adı', aciklama: 'Görüntülenecek ad (örn. "Zemin Kat", "1. Kat", "Bodrum -1")', zorunlu: true },
                { ad: 'Sıra', aciklama: 'Katların listede görünme sırası — küçük sayı yukarıda görünür', zorunlu: true },
                { ad: 'Aktif', aciklama: 'Pasif yapılan katlar daire formunda görünmez' },
            ]} />

            <Gorsel aciklama="Katlar tanımlama sayfası — kat listesi ve yeni kat ekleme formu" />

            <BaslikH3>Cephe Tanımlama</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Cepheler dairenin baktığı yönü belirtir (Kuzey, Güney, Doğu, Batı veya özel tanımlar).
                <strong>Ayarlar &rsaquo; Proje Tanımlamaları &rsaquo; Cepheler</strong> menüsünden yönetilir.
            </p>

            <Adim no={1} baslik="Cepheler sayfasına gidin">
                Ayarlar &rsaquo; Proje Tanımlamaları &rsaquo; <strong>Cepheler</strong> bölümünü açın.
            </Adim>
            <Adim no={2} baslik="Cephe ekleyin">
                <strong>+ Yeni Cephe</strong> butonuna tıklayın. Cephe adını (örn. "Kuzey", "Güney", "Deniz Manzarası") girin ve kaydedin.
            </Adim>

            <Gorsel aciklama="Cepheler tanımlama sayfası — cephe listesi ve ekleme formu" />

            <BaslikH3>Manzara Tanımlama</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Manzaralar dairenin görünümünü tanımlar (Deniz, Şehir, Orman, Bahçe vb.).
                <strong>Ayarlar &rsaquo; Proje Tanımlamaları &rsaquo; Manzaralar</strong> menüsünden yönetilir.
            </p>

            <Adim no={1} baslik="Manzaralar sayfasına gidin">
                Ayarlar &rsaquo; Proje Tanımlamaları &rsaquo; <strong>Manzaralar</strong> bölümünü açın.
            </Adim>
            <Adim no={2} baslik="Manzara ekleyin">
                <strong>+ Yeni Manzara</strong> butonuna tıklayın. Manzara adını (örn. "Deniz Manzarası", "Şehir Manzarası", "İç Bahçe") girin ve kaydedin.
            </Adim>

            <Gorsel aciklama="Manzaralar tanımlama sayfası — manzara listesi ve ekleme formu" />

            <Ipucu tur="uyari">
                Kat, Cephe ve Manzara tanımlamaları <strong>tüm projeler için ortaktır</strong>. Bir projede kullanılmayan seçenekler daire eklerken de listede görünür; ihtiyaç duyulmayan girişleri "Pasif" olarak işaretleyebilirsiniz.
            </Ipucu>

            <Ipucu tur="basari">
                Tüm tanımlamalar tamamlandıktan sonra artık <strong>Daire Ekleme</strong> adımına geçebilirsiniz.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       3. Daire Ekleme, Bilgileri Güncelleme
    ════════════════════════════════════════════════════════════ */
    'proje-yonetimi/daire-ekleme-guncelleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Blok, kat, cephe ve manzara tanımlamaları tamamlandıktan sonra projenize daire ekleyebilirsiniz.
                Her daire bağımsız bir takip kaydı ile yönetilir.
            </p>

            <BaslikH3>Daire Ekleme</BaslikH3>

            <Adim no={1} baslik="Proje detayına gidin">
                Projeler listesinden ilgili projeye tıklayın. Proje detay sayfasında <strong>Daireler</strong> sekmesini seçin.
            </Adim>

            <Gorsel aciklama="Proje detayı — Daireler sekmesi, mevcut daireler ızgara/liste görünümde" />

            <Adim no={2} baslik="'+ Daire Ekle' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Daire Ekle</strong> butonuna tıklayın. Daire oluşturma formu açılır.
            </Adim>

            <Adim no={3} baslik="Daire bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Daire No', aciklama: 'Dairenin numarası veya kodu (örn. "A101", "B205")', zorunlu: true },
                    { ad: 'Blok', aciklama: 'Dairenin bulunduğu blok', zorunlu: true },
                    { ad: 'Kat', aciklama: 'Dairenin bulunduğu kat', zorunlu: true },
                    { ad: 'Cephe', aciklama: 'Dairenin baktığı cephe', zorunlu: true },
                    { ad: 'Manzara', aciklama: 'Dairenin manzarası', zorunlu: true },
                    { ad: 'Daire Tipi', aciklama: '1+1, 2+1, 3+1, Dubleks vb.', zorunlu: true },
                    { ad: 'Liste Fiyatı', aciklama: 'Dairenin satış fiyatı' },
                    { ad: 'Net m²', aciklama: 'Net kullanım alanı' },
                    { ad: 'Brüt m²', aciklama: 'Brüt alan (ortak alanlar dahil)' },
                    { ad: 'Para Birimi', aciklama: 'TL, USD veya EUR' },
                    { ad: 'Açıklama', aciklama: 'Daireye özel notlar' },
                ]} />
            </Adim>

            <Gorsel aciklama="Daire ekleme formu — tüm alanlar görünür hâlde" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna tıklayın. Daire oluşturulur ve listede görünmeye başlar.
                Sistem otomatik olarak dairenin <strong>Takip No</strong>'sunu oluşturur.
            </Adim>

            <Ipucu tur="bilgi">
                Daire durumu varsayılan olarak <strong>"Müsait"</strong> gelir. Rezerve edildiğinde veya sözleşme imzalandığında durum otomatik güncellenir.
            </Ipucu>

            <BaslikH3>Daire Bilgilerini Güncelleme</BaslikH3>

            <Adim no={1} baslik="Daireye tıklayın">
                Daireler listesinde veya görünüm planında ilgili daireye tıklayın. Daire detay sayfası açılır.
            </Adim>

            <Gorsel aciklama="Daire detay sayfası — bilgiler, durum göstergesi ve düzenleme butonu" />

            <Adim no={2} baslik="Düzenle butonuna tıklayın">
                Sayfanın üst kısmındaki <strong>Düzenle</strong> butonuna tıklayın. Alanlar düzenlenebilir hale gelir.
            </Adim>

            <Adim no={3} baslik="Değişikliklerinizi yapın ve kaydedin">
                İstediğiniz alanları güncelleyip <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="uyari">
                <strong>Sözleşme imzalanmış</strong> dairelerin fiyat ve tip bilgisi değiştirildiğinde mevcut sözleşme etkilenmez;
                sözleşme kendi fiyat kaydını tutar.
            </Ipucu>

            <BaslikH3>Daire Durumları</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: '🟢 Müsait', aciklama: 'Satışa açık, henüz rezervasyon veya sözleşme yok' },
                { ad: '🟡 Rezerve', aciklama: 'Geçici rezervasyon yapılmış, sözleşme imzalanmamış' },
                { ad: '🔴 Satıldı', aciklama: 'Sözleşme imzalanmış ve aktif' },
                { ad: '⚫ İptal', aciklama: 'Sözleşme iptal edilmiş, tekrar satışa açılabilir' },
            ]} />

            <BaslikH3>Daire Özellikleri Ekleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Daire detay sayfasında <strong>Özellikler</strong> sekmesine geçerek daireye özel özellikler ekleyebilirsiniz
                (örn. "Balkon: 12 m²", "Ebeveyn Banyosu: Var", "Otopark: 1 Araçlık").
            </p>

            <Gorsel aciklama="Daire detayı — Özellikler sekmesi, özellik listesi ve ekleme formu" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       4. Toplu Fiyat Güncelleme
    ════════════════════════════════════════════════════════════ */
    'proje-yonetimi/toplu-fiyat-guncelleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Toplu fiyat güncelleme özelliği ile bir projedeki birden fazla dairenin liste fiyatını
                tek seferde belirli bir oran veya sabit tutar kadar artırıp azaltabilirsiniz.
            </p>

            <BaslikH3>Toplu Fiyat Güncelleme Adımları</BaslikH3>

            <Adim no={1} baslik="Proje detay sayfasını açın">
                Projeler listesinden ilgili projeye tıklayın ve <strong>Daireler</strong> sekmesine geçin.
            </Adim>

            <Adim no={2} baslik="Toplu Fiyat Güncelle butonuna tıklayın">
                Daireler listesinin üst kısmında yer alan <strong>Toplu Fiyat Güncelle</strong> butonuna tıklayın.
                Güncelleme paneli açılır.
            </Adim>

            <Gorsel aciklama="Daireler sekmesi — 'Toplu Fiyat Güncelle' butonu vurgulanmış ve güncelleme paneli açık hâlde" />

            <Adim no={3} baslik="Güncelleme türünü seçin">
                İki tür güncelleme yapabilirsiniz:
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                        <strong style={{ color: '#166534' }}>Yüzde ile Artır/Azalt:</strong> Tüm seçili dairelerin fiyatını belirttiğiniz yüzde oranında değiştirir.
                        Örneğin %10 artış girerek tüm fiyatları %10 yükseltebilirsiniz.
                    </div>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                        <strong style={{ color: '#1e40af' }}>Sabit Tutar ile Artır/Azalt:</strong> Seçili dairelerin fiyatına sabit bir miktar ekler veya çıkarır.
                    </div>
                </div>
            </Adim>

            <Adim no={4} baslik="Daireleri filtreleyin ve seçin">
                Güncelleme yapmak istediğiniz daireleri aşağıdaki kriterlere göre daraltabilirsiniz:
                <AlanTablosu alanlar={[
                    { ad: 'Blok', aciklama: 'Sadece belirli bloktaki daireler' },
                    { ad: 'Kat', aciklama: 'Sadece belirli kattaki daireler' },
                    { ad: 'Daire Tipi', aciklama: 'Sadece belirli tipteki daireler (örn. sadece 3+1\'ler)' },
                    { ad: 'Durum', aciklama: 'Sadece müsait daireler (satılmış dairelere genellikle uygulanmaz)' },
                ]} />
            </Adim>

            <Gorsel aciklama="Toplu fiyat güncelleme paneli — filtre seçenekleri, güncelleme türü ve önizleme tablosu" />

            <Adim no={5} baslik="Önizlemeyi kontrol edin">
                Güncelleme öncesinde sistem <strong>mevcut fiyat → yeni fiyat</strong> şeklinde bir önizleme tablosu gösterir.
                Değişikliklerin doğru olduğunu kontrol edin.
            </Adim>

            <Adim no={6} baslik="Uygula butonuna tıklayın">
                <strong>Güncelle</strong> butonuna tıklayın. Seçili dairelerin fiyatları toplu olarak güncellenir.
            </Adim>

            <Ipucu tur="uyari">
                Toplu fiyat güncelleme işlemi <strong>geri alınamaz</strong>. Uygulamadan önce önizleme ekranını dikkatlice inceleyiniz.
            </Ipucu>

            <Ipucu tur="bilgi">
                Fiyat güncellemesi sadece <strong>liste fiyatını</strong> değiştirir. Daha önce imzalanmış sözleşmelerdeki fiyatlar etkilenmez.
            </Ipucu>

            <BaslikH3>Fiyat Geçmişi</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Her dairenin detay sayfasında <strong>Fiyat Geçmişi</strong> sekmesinde yapılan tüm fiyat değişiklikleri,
                kim tarafından ne zaman yapıldığı bilgisiyle birlikte kayıt altındadır.
            </p>

            <Gorsel aciklama="Daire detayı — Fiyat Geçmişi sekmesi, tarih/fiyat/kullanıcı kolonlu tablo" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       5. Sunum Belgeleri, Özellikler, İmalat Özellikleri, Görseller
    ════════════════════════════════════════════════════════════ */
    'proje-yonetimi/sunum-belgeler-ozellikler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Proje detay sayfasında birden fazla sekme üzerinden projeye ait satış ve pazarlama materyalleri yönetilir:
                Sunum Belgeleri, Özellikler, İmalat Özellikleri ve Görseller.
            </p>

            <BaslikH3>Sunum Belgeleri</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Müşteri sunumlarında kullanılacak PDF, broşür ve teknik çizim gibi dosyalar buraya yüklenir.
                Bu belgeler müşteri sunumu oluşturulurken seçilebilir ve müşteriye paylaşılabilir.
            </p>

            <Adim no={1} baslik="Proje detayında Sunum Belgeleri sekmesini açın">
                Proje detay sayfasında <strong>Sunum Belgeleri</strong> sekmesine tıklayın.
            </Adim>
            <Adim no={2} baslik="Belge yükleyin">
                <strong>+ Belge Ekle</strong> butonuna tıklayın. Dosyayı seçin, belge türünü belirtin ve açıklama ekleyin.
                PDF, JPEG, PNG ve Word (.docx) formatları desteklenir.
            </Adim>

            <Gorsel aciklama="Proje detayı — Sunum Belgeleri sekmesi, yüklü belgeler listesi ve yükleme formu" />

            <AlanTablosu alanlar={[
                { ad: 'Belge Türü', aciklama: '"Teknik Çizim", "Broşür", "Fiyat Listesi", "Kat Planı" vb.', zorunlu: true },
                { ad: 'Açıklama', aciklama: 'Belgeyi tanımlayan kısa not' },
                { ad: 'Dosya', aciklama: 'Yüklenecek dosya (maks. 10 MB)', zorunlu: true },
            ]} />

            <Ipucu tur="bilgi">
                Yüklenen belgeler müşteri sunumları oluştururken <strong>Sunum Belgeleri</strong> sekmesinden seçilerek sunuma eklenebilir.
            </Ipucu>

            <BaslikH3>Proje Özellikleri</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Projeye ait genel özellikler (örn. "Kapalı Otopark: 2 Katlı", "Sosyal Tesis: Yüzme Havuzu", "Isıtma: Doğalgaz Kombi")
                bu bölümde tanımlanır. Müşteri sunumlarında ve fiyat listelerinde gösterilir.
            </p>

            <Adim no={1} baslik="Özellikler sekmesini açın">
                Proje detayında <strong>Özellikler</strong> sekmesine geçin.
            </Adim>
            <Adim no={2} baslik="Özellik ekleyin">
                <strong>+ Özellik Ekle</strong> butonuna tıklayın. Özellik adını ve değerini girerek kaydedin.
            </Adim>

            <Gorsel aciklama="Proje özellikler sekmesi — özellik-değer çiftleri listesi ve ekleme formu" />

            <BaslikH3>İmalat Özellikleri</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Yapı malzemeleri, iç dekorasyon detayları ve teknik özellikler (örn. "Dış Cephe: Mantolama", "Zemin: Granit",
                "Pencere: Isıcamlı PVC") bu sekme üzerinden yönetilir.
            </p>

            <Adim no={1} baslik="İmalat Özellikleri sekmesini açın">
                Proje detayında <strong>İmalat Özellikleri</strong> sekmesine geçin.
            </Adim>
            <Adim no={2} baslik="Kategori oluşturun">
                Önce bir kategori oluşturun (örn. "Dış Cephe", "İç Mekan", "Teknik Donanım").
                Kategoriler bilgilerin düzenli görünmesini sağlar.
            </Adim>
            <Adim no={3} baslik="Özellik notları ekleyin">
                Her kategorinin altına özellik satırları ekleyin. Bu notlar sözleşme belgelerine aktarılabilir.
            </Adim>

            <Gorsel aciklama="İmalat Özellikleri sekmesi — kategori yapısı ve özellik satırları, sözleşmeye aktar butonu" />

            <Ipucu tur="bilgi">
                İmalat özellikleri <strong>Döküman Şablonları</strong> üzerinden hazırlanan sözleşme belgelerine
                otomatik olarak aktarılabilir.
            </Ipucu>

            <BaslikH3>Proje Görselleri</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Projeye ait render, fotoğraf ve vaat görselleri <strong>Görseller</strong> sekmesine yüklenir.
                Müşteri sunumlarında ve paylaşım linklerinde otomatik kullanılır.
            </p>

            <Adim no={1} baslik="Görseller sekmesini açın">
                Proje detayında <strong>Görseller</strong> sekmesine geçin.
            </Adim>
            <Adim no={2} baslik="Görsel yükleyin">
                <strong>+ Görsel Ekle</strong> butonuna tıklayın veya görselleri sürükleyip bırakın.
                JPG ve PNG formatı, maksimum 10 MB desteklenir.
            </Adim>
            <Adim no={3} baslik="Vitrin görseli belirleyin">
                Yüklenen görseller arasından yıldız simgesine tıklayarak <strong>vitrin görseli</strong> seçin.
                Vitrin görseli proje kartında ve sunumlarda ön plana çıkar.
            </Adim>

            <Gorsel aciklama="Görseller sekmesi — yüklü görseller ızgara görünümde, vitrin yıldızı işaretli" />

            <Ipucu tur="uyari">
                Yüksek çözünürlüklü görseller yavaş internet bağlantılarında yüklenme süresini uzatabilir.
                Web için optimize edilmiş (72 DPI, maks. 2000px genişlik) görseller yüklemeniz önerilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       6. Görünümler
    ════════════════════════════════════════════════════════════ */
    'proje-yonetimi/gorunumler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Görünümler özelliği, projenizin kat planını veya blok cephe görselini etkileşimli bir haritaya dönüştürür.
                Her daire görsel üzerinde konumlandırılır; tıklandığında daire bilgileri, durumu ve fiyatı görüntülenir.
            </p>

            <BaslikH3>Görünüm Nedir?</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Görünüm; bir kat planı veya cephe görseli üzerinde dairelerin işaretlendiği etkileşimli bir plandır.
                Renk kodlamasıyla dairelerin satış durumu (müsait/rezerve/satıldı) anlık olarak takip edilebilir.
                Müşteri sunumlarında da kullanılabilir.
            </p>

            <Gorsel aciklama="Tamamlanmış bir görünüm örneği — kat planı üzerinde renkli daire bölgeleri, sağda bilgi paneli" />

            <BaslikH3>Yeni Görünüm Oluşturma</BaslikH3>

            <Adim no={1} baslik="Proje detayında Görünümler sekmesini açın">
                Proje detay sayfasında <strong>Görünümler</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Görünüm' butonuna tıklayın">
                Açılan formda görünüme bir isim verin (örn. "A Blok Zemin Kat", "B Blok 3. Kat") ve
                kullanılacak kat planı görselini yükleyin.
            </Adim>

            <Gorsel aciklama="Yeni görünüm oluşturma formu — isim alanı ve görsel yükleme alanı" />

            <Adim no={3} baslik="Görünüm editörünü açın">
                Görünüm oluşturulduktan sonra <strong>Düzenle</strong> butonuna tıklayın.
                Görünüm editörü açılır; sol tarafta kat planı görseli, sağ tarafta daire listesi yer alır.
            </Adim>

            <Gorsel aciklama="Görünüm editörü — sol taraf kat planı görseli, sağ taraf daire listesi ve araç çubuğu" />

            <Adim no={4} baslik="Daireleri plana yerleştirin">
                Sağ panelden bir daire seçin, ardından kat planı üzerinde dairenin konumuna tıklayıp sürükleyerek
                bölgeyi çizin. Her daire için bu işlemi tekrarlayın.
            </Adim>

            <Gorsel aciklama="Görünüm editörü — bir daire seçili, kat planı üzerinde bölge çizme işlemi devam ediyor" />

            <Adim no={5} baslik="Kaydedin">
                Tüm daireler yerleştirildikten sonra <strong>Kaydet</strong> butonuna tıklayın.
            </Adim>

            <Ipucu tur="bilgi">
                Görünümde her daire, mevcut satış durumuna göre <strong>otomatik renklenir</strong>:
                <br />🟢 Yeşil = Müsait &nbsp;|&nbsp; 🟡 Sarı = Rezerve &nbsp;|&nbsp; 🔴 Kırmızı = Satıldı
            </Ipucu>

            <BaslikH3>Görünümü Sunum & Teklif'te Kullanma</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Müşteri sunumu oluştururken <strong>Görünümler</strong> sekmesinden bu plana erişilebilir.
                Müşteriye paylaşılan link üzerinden de plan görüntülenebilir; tıklanan dairenin fiyat ve bilgileri
                anlık olarak gösterilir.
            </p>

            <Gorsel aciklama="Müşteri görünüm linki — tarayıcıda açık kat planı, daire üzerine gelindiğinde fiyat balonu" />

            <Ipucu tur="uyari">
                Görünüm oluşturabilmek için önce projeye ait <strong>kat planı görseli hazır olmalı</strong>
                ve ilgili daireler sisteme eklenmiş olmalıdır.
            </Ipucu>

            <BaslikH3>Görünüm Güncelleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Mevcut bir görünümü güncellemek için Görünümler sekmesinden ilgili görünümün yanındaki
                <strong> Düzenle</strong> butonuna tıklayın. Yeni daire ekleyebilir, mevcut konumları düzeltebilir
                veya görseli değiştirebilirsiniz.
            </p>

            <Gorsel aciklama="Görünümler sekmesi — görünüm listesi, her satırda 'Düzenle' ve 'Sil' butonları" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       POTANSİYEL MÜŞTERİ — Ekleme
    ════════════════════════════════════════════════════════════ */
    'potansiyel-musteri/ekleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Potansiyel Müşteri Takip modülü, satın alma ilgisi olan kişileri kayıt altına almanızı,
                durumlarını takip etmenizi ve görüşme geçmişini yönetmenizi sağlar.
                Kayıtlar liste görünümü veya Kanban pano üzerinden izlenebilir.
            </p>

            <BaslikH3>Manuel Potansiyel Müşteri Ekleme</BaslikH3>

            <Adim no={1} baslik="Potansiyel Müşteri Takip sayfasını açın">
                Sol menüden <strong>Potansiyel Müşteri Takip</strong> bağlantısına tıklayın.
                Sayfa açıldığında mevcut kayıtlar liste veya Kanban görünümünde görüntülenir.
            </Adim>

            <Gorsel aciklama="Potansiyel Müşteri Takip sayfası — liste görünümü, sağ üstte '+ Yeni Kayıt' butonu" />

            <Adim no={2} baslik="'+ Yeni Kayıt' butonuna tıklayın">
                Sayfanın sağ üst köşesindeki <strong>+ Yeni Kayıt</strong> butonuna tıklayın.
                Yeni kayıt formu açılır.
            </Adim>

            <Adim no={3} baslik="Bilgileri doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Ad Soyad', aciklama: 'Potansiyel müşterinin adı ve soyadı', zorunlu: true },
                    { ad: 'Telefon', aciklama: 'İletişim telefon numarası' },
                    { ad: 'E-posta', aciklama: 'E-posta adresi' },
                    { ad: 'Kaynak', aciklama: 'Müşterinin nereden geldiği: Web Formu, Tavsiye, Sosyal Medya, Telefon vb.' },
                    { ad: 'Durum', aciklama: 'Başlangıç durumu — genellikle "Yeni" olarak bırakılır' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni potansiyel müşteri ekleme formu — tüm alanlar dolu hâlde" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna tıklayın. Kayıt oluşturulur ve listeye eklenir.
                Henüz herhangi bir kullanıcıya atanmamışsa <strong>AssignedUser</strong> boş kalır;
                ilk arama kaydını ekleyen kullanıcıya otomatik atanır.
            </Adim>

            <Ipucu tur="basari">
                İlk görüşme kaydı girildiğinde kayıt <strong>o kullanıcıya otomatik atanır</strong>.
                Bu sayede kimin hangi potansiyel müşteriyle ilgilendiği takip edilebilir.
            </Ipucu>

            <BaslikH3>Durum Açıklamaları</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: '🔵 Yeni', aciklama: 'Henüz iletişime geçilmemiş, yeni gelen kayıt' },
                { ad: '📞 Arandı', aciklama: 'En az bir kez aranmış veya görüşme yapılmış' },
                { ad: '⏳ Beklemede', aciklama: 'İlgili ancak karar verme aşamasında değil' },
                { ad: '✅ İlgili', aciklama: 'Aktif ilgi gösteren, satışa yakın müşteri' },
                { ad: '❌ İlgisiz', aciklama: 'İlgi göstermeyen veya vazgeçen müşteri' },
            ]} />

            <BaslikH3>Kanban Görünümü</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Sayfanın üst kısmındaki görünüm geçiş butonuyla <strong>Kanban</strong> moduna geçebilirsiniz.
                Her sütun bir durumu temsil eder; kartları sürükleyip bırakarak durum güncelleyebilirsiniz.
            </p>

            <Gorsel aciklama="Kanban görünümü — 5 sütun (Yeni / Arandı / Beklemede / İlgili / İlgisiz), sürüklenebilir kartlar" />

            <BaslikH3>Kayıt Detayını Açma</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Liste veya Kanban'da herhangi bir kayda tıklandığında detay modalı açılır.
                Modal üç sekme içerir: <strong>Müşteri Bilgileri</strong>, <strong>Görüşme Geçmişi</strong> ve <strong>Yeni Görüşme Ekle</strong>.
            </p>

            <Gorsel aciklama="Potansiyel müşteri detay modalı — üç sekme görünür, Müşteri Bilgileri sekmesi açık" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       POTANSİYEL MÜŞTERİ — Arama Kaydı
    ════════════════════════════════════════════════════════════ */
    'potansiyel-musteri/arama-kaydi': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Her potansiyel müşteriyle yapılan görüşmeler, aramalar ve takip notları <strong>Arama Kaydı</strong> olarak sisteme eklenir.
                Bu kayıtlar hem görüşme geçmişini oluşturur hem de bir sonraki arama tarihini planlamayı sağlar.
            </p>

            <BaslikH3>Yeni Görüşme / Arama Kaydı Ekleme</BaslikH3>

            <Adim no={1} baslik="Potansiyel müşteri kaydını açın">
                Liste veya Kanban'dan ilgili kayda tıklayın. Detay modalı açılır.
            </Adim>

            <Adim no={2} baslik="'Yeni Görüşme Ekle' sekmesine geçin">
                Modal içinde <strong>Yeni Görüşme Ekle</strong> sekmesine tıklayın.
            </Adim>

            <Gorsel aciklama="Detay modalı — 'Yeni Görüşme Ekle' sekmesi açık, not alanı ve tarih seçici görünür" />

            <Adim no={3} baslik="Görüşme bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Görüşme Notu', aciklama: 'Yapılan görüşmenin özeti, müşteri talepleri, söylenenler', zorunlu: true },
                    { ad: 'Tekrar Arama Tarihi', aciklama: 'Bir sonraki görüşme için hatırlatma tarihi — boş bırakılabilir' },
                    { ad: 'Tamamlandı', aciklama: 'Görüşme tamamlandıysa işaretleyin; işaretlenmezse "Bekliyor" olarak kalır' },
                ]} />
            </Adim>

            <Adim no={4} baslik="Kaydedin">
                <strong>Görüşmeyi Kaydet</strong> butonuna tıklayın. Kayıt oluşturulur ve <strong>Görüşme Geçmişi</strong> sekmesine eklenir.
            </Adim>

            <Ipucu tur="bilgi">
                Görüşme kaydı ekleyen kullanıcı bu kayda daha önce hiç işlem yapmadıysa ve kayıt herhangi birine atanmamışsa,
                sistem bu kullanıcıyı <strong>otomatik olarak kayda atar</strong>.
            </Ipucu>

            <BaslikH3>Tekrar Arama Tarihi</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Görüşme kaydederken veya bağımsız olarak <strong>Tekrar Arama Tarihi</strong> belirleyebilirsiniz.
                Belirlenen tarih geldiğinde görev listenizde ve ajandanızda hatırlatıcı olarak görünür.
            </p>

            <Gorsel aciklama="Detay modalı altı — bağımsız 'Tekrar Arama Tarihi' seçici ve 'Tarihi Kaydet' butonu" />

            <Ipucu tur="bilgi">
                Tekrar arama tarihi görüşme notu olmadan da bağımsız olarak kaydedilebilir.
                Bu tarihler <strong>Ajanda</strong> modülünde ve günlük görev listenizde görünür.
            </Ipucu>

            <BaslikH3>Görüşme Geçmişini İnceleme</BaslikH3>

            <Adim no={1} baslik="Görüşme Geçmişi sekmesini açın">
                Detay modalında <strong>Görüşme Geçmişi</strong> sekmesine tıklayın.
                Tüm görüşmeler tarih sırasına göre listelenir.
            </Adim>

            <Gorsel aciklama="Görüşme Geçmişi sekmesi — tarih, notlar, kullanıcı adı ve tamamlanma durumu ile görüşmeler listesi" />

            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Her görüşme kaydında şu bilgiler görünür:
            </p>
            <AlanTablosu alanlar={[
                { ad: 'Tarih', aciklama: 'Görüşmenin yapıldığı tarih' },
                { ad: 'Not', aciklama: 'Görüşme özeti' },
                { ad: 'Kullanıcı', aciklama: 'Görüşmeyi kaydeden kullanıcı' },
                { ad: 'Tekrar Arama', aciklama: 'Belirlendiyse bir sonraki arama tarihi' },
                { ad: 'Durum', aciklama: '"Tamamlandı" veya "Bekliyor"' },
            ]} />

            <BaslikH3>Görüşmeyi Tamamlandı Olarak İşaretleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Görüşme geçmişindeki her kayıt üzerinde <strong>Tamamlandı</strong> onay kutusunu işaretleyerek
                durumu güncelleyebilirsiniz. Bu durum, Potansiyel Müşteri Raporu'ndaki tamamlanma istatistiklerini etkiler.
            </p>

            <Gorsel aciklama="Görüşme geçmişi — bir görüşme kaydı üzerinde 'Tamamlandı' onay kutusu işaretleniyor" />

            <BaslikH3>Müşteri Olarak Ekleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Potansiyel müşteri satın alma kararı verdiğinde <strong>Müşteri Bilgileri</strong> sekmesindeki
                <strong> Müşteri Olarak Ekle</strong> butonu ile sistemde tam müşteri kaydına dönüştürülebilir.
                Tüm görüşme notları yeni müşteri kartına aktarılır.
            </p>

            <Gorsel aciklama="Müşteri Bilgileri sekmesi — 'Müşteri Olarak Ekle' butonu vurgulanmış" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       POTANSİYEL MÜŞTERİ — API ve Web Form
    ════════════════════════════════════════════════════════════ */
    'potansiyel-musteri/api-web-form': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                YapiCRM, web sitenizdeki iletişim formlarından ve harici sistemlerden gelen potansiyel müşteri
                başvurularını otomatik olarak sisteme aktarmanızı sağlar.
                Bu entegrasyon <strong>Potansiyel Müşteri Ayarları</strong> ekranından yapılandırılır.
            </p>

            <BaslikH3>API Ayarlarına Erişim</BaslikH3>

            <Adim no={1} baslik="Ayarlar > Potansiyel Müşteri Ayarları sayfasını açın">
                Üst menüden <strong>Ayarlar</strong>'a tıklayın.
                <strong>Potansiyel Müşteri Ayarları</strong> sekmesine geçin.
                Bu sekmede <strong>Lead API Ayarları</strong> bölümü yer alır.
            </Adim>

            <Gorsel aciklama="Ayarlar > Potansiyel Müşteri Ayarları sekmesi — API anahtarı ve endpoint bilgileri" />

            <BaslikH3>API Anahtarı Oluşturma</BaslikH3>

            <Adim no={2} baslik="Yeni API Kullanıcısı Ekleyin">
                <strong>+ Yeni API Kullanıcısı</strong> butonuna tıklayın.
                Her entegrasyon kaynağı için ayrı bir API kullanıcısı oluşturabilirsiniz
                (örn. "Web Sitesi Formu", "Sahibinden.com", "Hürriyet Emlak").
            </Adim>

            <AlanTablosu alanlar={[
                { ad: 'Kullanıcı Adı', aciklama: 'Entegrasyonu tanımlayan isim (örn. "websitesi-form")', zorunlu: true },
                { ad: 'Kaynak Etiketi', aciklama: 'Bu kaynaktan gelen leadlerin "Kaynak" alanında görünecek değer (örn. "Web Sitesi")', zorunlu: true },
                { ad: 'Aktif', aciklama: 'Pasif yapıldığında bu kaynaktan gelen istekler reddedilir' },
            ]} />

            <Gorsel aciklama="API Kullanıcısı ekleme formu — kullanıcı adı, kaynak etiketi alanları ve oluşturulan API anahtarı" />

            <Adim no={3} baslik="API Anahtarını kopyalayın">
                Kayıt oluşturulduktan sonra sistem bir <strong>API Anahtarı</strong> oluşturur.
                Bu anahtarı güvenli bir yere kopyalayın; web formunuzu entegre ederken kullanacaksınız.
            </Adim>

            <Ipucu tur="uyari">
                API anahtarı oluşturulduktan sonra tekrar görüntülenemez. Kaybedilirse yeni bir anahtar oluşturmanız gerekir.
            </Ipucu>

            <BaslikH3>Web Form Entegrasyonu</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Web sitenizde yer alan iletişim formu, aşağıdaki formatta bir HTTP POST isteği göndererek
                YapiCRM'e otomatik lead aktarabilir.
            </p>

            <Adim no={4} baslik="API endpoint'ini öğrenin">
                Lead eklemek için kullanılacak endpoint aşağıdaki formattadır:
                <div style={{ background: '#1e293b', color: '#e2e8f0', borderRadius: 9, padding: '14px 18px', margin: '10px 0', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.7 }}>
                    <span style={{ color: '#94a3b8' }}>POST</span>{' '}
                    <span style={{ color: '#7dd3fc' }}>/api/LeadApi/submit</span>
                    <br />
                    <span style={{ color: '#94a3b8' }}>Header:</span>{' '}
                    <span style={{ color: '#86efac' }}>X-Api-Key: &lt;API_ANAHTARINIZ&gt;</span>
                </div>
            </Adim>

            <Adim no={5} baslik="Gönderilecek veri formatı">
                Form verilerini aşağıdaki JSON formatında gönderin:
                <div style={{ background: '#1e293b', color: '#e2e8f0', borderRadius: 9, padding: '14px 18px', margin: '10px 0', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.8 }}>
                    {`{`}<br />
                    &nbsp;&nbsp;<span style={{ color: '#7dd3fc' }}>"fullName"</span>: <span style={{ color: '#86efac' }}>"Ad Soyad"</span>,<br />
                    &nbsp;&nbsp;<span style={{ color: '#7dd3fc' }}>"phone"</span>: <span style={{ color: '#86efac' }}>"05XX XXX XX XX"</span>,<br />
                    &nbsp;&nbsp;<span style={{ color: '#7dd3fc' }}>"email"</span>: <span style={{ color: '#86efac' }}>"ornek@mail.com"</span><br />
                    {`}`}
                </div>
                <AlanTablosu alanlar={[
                    { ad: 'fullName', aciklama: 'Ad ve soyadı', zorunlu: true },
                    { ad: 'phone', aciklama: 'Telefon numarası' },
                    { ad: 'email', aciklama: 'E-posta adresi' },
                ]} />
            </Adim>

            <Gorsel aciklama="Web sitesi iletişim formu örneği — form gönderildiğinde YapiCRM'e otomatik iletilir" />

            <Adim no={6} baslik="Gelen leadleri takip edin">
                API üzerinden gelen kayıtlar <strong>Potansiyel Müşteri Takip</strong> listesinde otomatik olarak görünür.
                <strong>Kaynak</strong> alanı, API kullanıcısı oluştururken girilen etiketle dolar (örn. "Web Sitesi").
            </Adim>

            <Gorsel aciklama="Potansiyel Müşteri listesi — kaynak sütununda 'Web Sitesi' etiketi ile gelen otomatik kayıtlar" />

            <Ipucu tur="bilgi">
                API üzerinden gelen kayıtların <strong>AssignedUserId</strong> alanı başlangıçta boştur.
                İlk görüşme kaydını ekleyen kullanıcıya otomatik atanır.
            </Ipucu>

            <BaslikH3>Entegrasyon Test Etme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Entegrasyonunuzu test etmek için API Ayarları sayfasındaki <strong>Test Et</strong> butonu ile
                örnek bir kayıt gönderebilirsiniz. Başarılı gönderim sonrası test kaydı Potansiyel Müşteri listesinde görünür.
            </p>

            <Gorsel aciklama="API Ayarları sayfası — 'Test Et' butonu ve son entegrasyon durumu göstergesi" />

            <Ipucu tur="uyari">
                Test sırasında oluşan kayıtları listeden manuel olarak silmeyi unutmayın.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       MÜŞTERİ SUNUMLARI — Oluşturma
    ════════════════════════════════════════════════════════════ */
    'musteri-sunumlari/olusturma': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Sunum & Teklif modülü, seçtiğiniz daire veya portföyleri müşteriye özgü bir sunum paketi hâlinde
                derlemenizi sağlar. Sunum oluşturulduktan sonra müşteriyle link üzerinden paylaşılabilir.
            </p>

            <BaslikH3>Yeni Sunum Oluşturma</BaslikH3>

            <Adim no={1} baslik="Sunum & Teklif sayfasını açın">
                Sol menüden <strong>Sunum & Teklif</strong> bağlantısına tıklayın.
                Mevcut sunumlar liste hâlinde görüntülenir.
            </Adim>

            <Gorsel aciklama="Sunum & Teklif listesi — sunum adı, müşteri, oluşturma tarihi, durum sütunları ve '+ Yeni Sunum' butonu" />

            <Adim no={2} baslik="'+ Yeni Sunum' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Sunum</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Sunum bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Sunumun adı (örn. "Terrace Park — Ahmet Bey Sunumu")', zorunlu: true },
                    { ad: 'Müşteri', aciklama: 'Sunumun yapılacağı müşteri — Müşteriler listesinden seçilir', zorunlu: true },
                    { ad: 'Açıklama', aciklama: 'Sunum hakkında kısa not' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni sunum oluşturma formu — başlık, müşteri seçimi ve açıklama alanları" />

            <Adim no={4} baslik="Daire veya portföy ekleyin">
                Sunum oluşturulduktan sonra detay sayfasına yönlendirilirsiniz.
                <strong>+ Daire Ekle</strong> veya <strong>+ Portföy Ekle</strong> butonuyla sunuma öğe ekleyin.
                Her öğe için not alanı ve müşteri görüş durumu belirlenebilir.
            </Adim>

            <Gorsel aciklama="Sunum detay sayfası — eklenen daireler listesi, her satırda not ve müşteri durum alanı" />

            <Adim no={5} baslik="Sunum belgelerini ekleyin">
                Sunuma broşür, teknik çizim veya fiyat listesi gibi belgeler eklemek için
                <strong> Sunum Belgeleri</strong> sekmesini kullanın.
            </Adim>

            <Ipucu tur="bilgi">
                Sunuma eklenen daireler, projedeki <strong>Görünümler</strong> üzerinde de işaretli hâlde gösterilir.
                Müşteri, paylaşım linki üzerinden kat planında dairelerin konumunu görebilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       MÜŞTERİ SUNUMLARI — Müşteri ile Paylaşma
    ════════════════════════════════════════════════════════════ */
    'musteri-sunumlari/paylasma': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Hazırlanan sunum, müşteriye özel bir link üzerinden paylaşılabilir.
                Müşteri bu link üzerinden daireye ait görselleri, planları ve fiyat bilgilerini görüntüleyebilir;
                beğeni/red bildirimi yapabilir.
            </p>

            <BaslikH3>Paylaşım Linki Oluşturma</BaslikH3>

            <Adim no={1} baslik="Sunum detay sayfasını açın">
                Sunum & Teklif listesinden ilgili sunuma tıklayın.
            </Adim>

            <Adim no={2} baslik="Paylaş butonuna tıklayın">
                Sayfanın üst kısmındaki <strong>Paylaş</strong> butonuna tıklayın.
                Sistem müşteriye özel bir link oluşturur.
            </Adim>

            <Gorsel aciklama="Sunum detayı — 'Paylaş' butonu ve oluşturulan link, kopyala butonu ile birlikte" />

            <Adim no={3} baslik="Linki müşteriye gönderin">
                Oluşturulan linki kopyalayıp WhatsApp, SMS veya e-posta ile müşteriye iletebilirsiniz.
            </Adim>

            <Ipucu tur="bilgi">
                Paylaşım linki <strong>şifre korumalı</strong> olabilir. Müşteri linki açtığında kimlik doğrulaması
                için telefon numarasını girmesi istenebilir.
            </Ipucu>

            <BaslikH3>Müşteri Etkileşimi Takibi</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Müşteri paylaşım linki üzerinden daireyi incelediğinde veya <strong>Beğendim / Beğenmedim</strong>
                seçimi yaptığında bu bilgiler sunum akışına kaydedilir.
            </p>

            <Gorsel aciklama="Sunum akış geçmişi — müşteri görüntüleme zamanları, beğeni/red kararları kronolojik sırada" />

            <AlanTablosu alanlar={[
                { ad: 'Görüntülendi', aciklama: 'Müşterinin linki kaç kez açtığı ve son görüntüleme zamanı' },
                { ad: 'Beğendi', aciklama: 'Müşteri daireyi beğendi olarak işaretledi' },
                { ad: 'Beğenmedi', aciklama: 'Müşteri daireyi reddetti' },
                { ad: 'Teklife Geçildi', aciklama: 'Bu daire için teklif aşamasına geçildi' },
            ]} />

            <BaslikH3>Sunum Durumu Güncelleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Sunum detayında her dairenin durumunu manuel olarak da güncelleyebilirsiniz:
                <strong> Gösterildi, Teklif Verildi, Kabul Edildi, Reddedildi</strong> gibi durumlar seçilebilir.
            </p>

            <Gorsel aciklama="Sunum detayı — her daire satırında durum dropdown menüsü açık hâlde" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       MÜŞTERİ SUNUMLARI — Online Görüşme
    ════════════════════════════════════════════════════════════ */
    'musteri-sunumlari/online-gorusme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Online Görüşme özelliği, müşteriyle gerçekleştirilen video veya telefon görüşmelerini
                planlayıp kayıt altına almanızı sağlar. Görüşme detayları, katılımcılar ve notlar sisteme eklenir.
            </p>

            <BaslikH3>Yeni Online Görüşme Oluşturma</BaslikH3>

            <Adim no={1} baslik="Sunum detayından görüşme oluşturun">
                Sunum detay sayfasında <strong>Online Görüşmeler</strong> sekmesine geçin.
                <strong>+ Yeni Görüşme</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Sunum detayı — Online Görüşmeler sekmesi, görüşme listesi ve '+ Yeni Görüşme' butonu" />

            <Adim no={2} baslik="Görüşme bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Görüşme konusu veya adı', zorunlu: true },
                    { ad: 'Tarih & Saat', aciklama: 'Görüşmenin planlandığı tarih ve saat', zorunlu: true },
                    { ad: 'Platform', aciklama: 'Görüşme platformu: Zoom, Teams, Google Meet, Telefon vb.' },
                    { ad: 'Görüşme Linki', aciklama: 'Video konferans bağlantısı' },
                    { ad: 'Notlar', aciklama: 'Görüşme öncesi veya sonrası notlar' },
                ]} />
            </Adim>

            <Gorsel aciklama="Online görüşme oluşturma formu — tarih seçici, platform ve link alanları" />

            <Adim no={3} baslik="Katılımcıları ekleyin">
                Görüşmeye katılacak kullanıcıları <strong>Katılımcılar</strong> alanından seçin.
                Birden fazla kullanıcı eklenebilir.
            </Adim>

            <Adim no={4} baslik="Görüşmeyi kaydedin">
                <strong>Kaydet</strong> butonuna basın. Görüşme planlandı olarak listelenir.
            </Adim>

            <BaslikH3>Görüşme Sonrası Not Ekleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Görüşme tamamlandıktan sonra ilgili kayda tıklayarak <strong>sonuç notu</strong> ekleyebilir
                ve görüşmeyi <strong>Tamamlandı</strong> olarak işaretleyebilirsiniz.
            </p>

            <Gorsel aciklama="Online görüşme detayı — sonuç notu alanı ve 'Tamamlandı olarak işaretle' butonu" />

            <Ipucu tur="bilgi">
                Online görüşmeler aynı zamanda <strong>Ajanda</strong> modülünde takvim üzerinde görüntülenir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       AJANDA — Yeni Not Oluşturma
    ════════════════════════════════════════════════════════════ */
    'ajanda/yeni-not': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Ajanda modülü, günlük notlarınızı ve hatırlatıcılarınızı takvim üzerinde takip etmenizi sağlar.
                Her kullanıcı kendi notlarını oluşturur; notlar renk kodlamasıyla kategorize edilebilir.
            </p>

            <BaslikH3>Yeni Not Oluşturma</BaslikH3>

            <Adim no={1} baslik="Ajanda sayfasını açın">
                Sol menüden <strong>Ajanda</strong> bağlantısına tıklayın.
                Takvim görünümü ve sağ tarafta bugünün görevleri listelenir.
            </Adim>

            <Gorsel aciklama="Ajanda sayfası — sol taraf aylık takvim, sağ taraf bugünün notları ve görevleri listesi" />

            <Adim no={2} baslik="Yeni Not butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Not</strong> butonuna tıklayın.
                Not oluşturma formu açılır.
            </Adim>

            <Adim no={3} baslik="Not bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Notun kısa başlığı (örn. "Ahmet Bey ile görüşme")', zorunlu: true },
                    { ad: 'Not İçeriği', aciklama: 'Notun detaylı açıklaması' },
                    { ad: 'Tarih', aciklama: 'Notun ait olduğu gün', zorunlu: true },
                    { ad: 'Renk', aciklama: 'Notun takvimde görüneceği renk — kategorize etmek için kullanın' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni not formu — başlık, içerik, tarih seçici ve renk seçici alanları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna tıklayın. Not takvimde ilgili güne eklenir.
            </Adim>

            <BaslikH3>Renk Kodlaması</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Notları renklendirerek kategorize edebilirsiniz. Örnek kullanım önerileri:
            </p>
            <AlanTablosu alanlar={[
                { ad: '🔴 Kırmızı', aciklama: 'Acil görevler, son tarihli işler' },
                { ad: '🟡 Sarı', aciklama: 'Hatırlatıcılar, bekleyen işler' },
                { ad: '🟢 Yeşil', aciklama: 'Tamamlanan notlar, başarılı görüşmeler' },
                { ad: '🔵 Mavi', aciklama: 'Planlama notları, toplantılar' },
                { ad: '🟣 Mor', aciklama: 'Kişisel notlar' },
            ]} />

            <Ipucu tur="bilgi">
                Potansiyel müşteri görüşmelerindeki <strong>Tekrar Arama Tarihi</strong> de ajanda takviminizde
                otomatik olarak görünür; bu görevleri manuel oluşturmanıza gerek yoktur.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       AJANDA — Kullanım
    ════════════════════════════════════════════════════════════ */
    'ajanda/kullanim': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Ajanda takvimi; kişisel notları, potansiyel müşteri tekrar arama tarihlerini ve online görüşmeleri
                tek bir ekranda birleştirir. Günlük ve aylık görünüm arasında geçiş yapılabilir.
            </p>

            <BaslikH3>Takvim Görünümü</BaslikH3>

            <Adim no={1} baslik="Aylık görünümü kullanın">
                Ajanda sayfası varsayılan olarak <strong>aylık takvim</strong> görünümünde açılır.
                Notlar ve görevler ilgili günlerin üzerinde renkli noktalar hâlinde gösterilir.
                Bir güne tıklandığında o günün kayıtları sağ panelde listelenir.
            </Adim>

            <Gorsel aciklama="Aylık takvim görünümü — bazı günlerde renkli noktalar ve sağ panelde seçili günün görevleri" />

            <Adim no={2} baslik="Güne tıklayın">
                Takvimdeki herhangi bir güne tıkladığınızda o güne ait tüm notlar ve görevler sağ panelde listelenir.
                Buradan doğrudan <strong>+ Yeni Not</strong> ekleyebilir veya mevcut notu düzenleyebilirsiniz.
            </Adim>

            <Gorsel aciklama="Takvimde bir gün seçili — sağ panelde o günün notları, her satırda başlık, renk ve işlem butonları" />

            <BaslikH3>Günlük Görev Listesi</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Sayfanın sağ üst kısmında <strong>Bugünün Görevleri</strong> bölümü bulunur.
                Burası; bugüne ait notları, vadesi gelen potansiyel müşteri aramalarını ve planlanan görüşmeleri bir arada gösterir.
            </p>

            <Gorsel aciklama="Bugünün görevleri paneli — notlar, potansiyel müşteri aramaları ve online görüşmeler ayrı bölümlerde" />

            <BaslikH3>Not Düzenleme ve Silme</BaslikH3>

            <Adim no={1} baslik="Notu açın">
                Takvimden veya görev listesinden ilgili nota tıklayın.
            </Adim>
            <Adim no={2} baslik="Düzenle veya Sil">
                Not detayında <strong>Düzenle</strong> butonuyla içeriği güncelleyebilir,
                <strong> Sil</strong> butonuyla notu silebilirsiniz.
            </Adim>

            <Gorsel aciklama="Not detayı — başlık, içerik, tarih bilgisi ile 'Düzenle' ve 'Sil' butonları" />

            <Ipucu tur="uyari">
                Silinen notlar geri getirilemez. Kalıcı silmeden önce emin olun.
            </Ipucu>

            <BaslikH3>Sonraki Aya Geçiş</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Takvimin üst kısmındaki <strong>&#8249;</strong> ve <strong>&#8250;</strong> okları ile önceki ve
                sonraki aya geçebilirsiniz. <strong>Bugün</strong> butonuna tıklayarak hızlıca mevcut aya dönebilirsiniz.
            </p>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       MÜŞTERİLER — Yeni Müşteri Ekleme
    ════════════════════════════════════════════════════════════ */
    'musteriler/yeni-musteri': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Müşteriler modülü, sözleşme imzalayan veya finansal işlem yapılan kişi ve firmalar için
                merkezi bir kayıt sistemidir. Her müşteri kaydına finansal hareketler, sözleşmeler, notlar ve dosyalar bağlanır.
            </p>

            <BaslikH3>Yeni Müşteri Ekleme</BaslikH3>

            <Adim no={1} baslik="Müşteriler sayfasını açın">
                Sol menüden <strong>Müşteriler</strong> bağlantısına tıklayın.
                Mevcut müşteri listesi görüntülenir.
            </Adim>

            <Gorsel aciklama="Müşteriler listesi — ad soyad, telefon, bakiye sütunları ve sağ üstte '+ Yeni Müşteri' butonu" />

            <Adim no={2} baslik="'+ Yeni Müşteri' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Müşteri</strong> butonuna tıklayın. Müşteri oluşturma formu açılır.
            </Adim>

            <Adim no={3} baslik="Müşteri bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Ad Soyad', aciklama: 'Müşterinin adı ve soyadı', zorunlu: true },
                    { ad: 'Firma Adı', aciklama: 'Kurumsal müşteriler için şirket adı' },
                    { ad: 'Telefon 1', aciklama: 'Ana iletişim numarası', zorunlu: true },
                    { ad: 'Telefon 2', aciklama: 'İkinci telefon numarası' },
                    { ad: 'E-posta', aciklama: 'E-posta adresi' },
                    { ad: 'Adres', aciklama: 'İkametgah veya iş adresi' },
                    { ad: 'TC Kimlik No', aciklama: 'T.C. kimlik numarası (sözleşmelerde kullanılır)' },
                    { ad: 'Vergi Dairesi', aciklama: 'Kurumsal müşteriler için vergi dairesi' },
                    { ad: 'Vergi No', aciklama: 'Kurumsal müşteriler için vergi numarası' },
                    { ad: 'Tür', aciklama: '"Müşteri" veya "Tedarikçi"', zorunlu: true },
                    { ad: 'Alt Kategori', aciklama: 'Müşteri segmentasyonu için ek sınıflandırma' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni müşteri formu — kişisel ve kurumsal bilgi alanları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna tıklayın. Müşteri kaydı oluşturulur ve otomatik bir
                <strong> Takip No</strong> atanır.
            </Adim>

            <Ipucu tur="basari">
                Potansiyel müşteri kaydındaki <strong>Müşteri Olarak Ekle</strong> butonunu kullanırsanız
                kişi bilgileri otomatik aktarılır, görüşme notları da yeni müşteri kartına kopyalanır.
            </Ipucu>

            <BaslikH3>Etiket Ekleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Müşteri kaydına <strong>Etiket</strong> ekleyerek segmentasyon yapabilirsiniz (örn. "VIP", "Yatırımcı", "Peşin").
                Etiketler liste üzerinde filtreleme için kullanılabilir.
            </p>

            <Gorsel aciklama="Müşteri detayı — etiket ekleme alanı, mevcut etiketler renkli chip olarak görünür" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       MÜŞTERİLER — Arama, Düzenleme
    ════════════════════════════════════════════════════════════ */
    'musteriler/arama-duzenleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Müşteri listesinde hızlı arama, filtreleme ve toplu işlemler yapılabilir.
                Müşteri kartına girildiğinde tüm finansal geçmiş, notlar ve bağlı dosyalar tek ekranda görüntülenir.
            </p>

            <BaslikH3>Müşteri Arama</BaslikH3>

            <Adim no={1} baslik="Arama kutusunu kullanın">
                Müşteriler listesinin üst kısmındaki <strong>Ara...</strong> kutusuna
                ad, telefon veya takip numarasını girin.
                Liste anlık olarak filtrelenir.
            </Adim>

            <Gorsel aciklama="Müşteri listesi — arama kutusu aktif, girilen metne göre filtrelenmiş sonuçlar" />

            <Adim no={2} baslik="Filtreler ile daraltın">
                <strong>Filtrele</strong> panelini açarak sonuçları daha da daraltabilirsiniz:
                <AlanTablosu alanlar={[
                    { ad: 'Tür', aciklama: 'Müşteri veya Tedarikçi' },
                    { ad: 'Alt Kategori', aciklama: 'Belirlediğiniz özel kategoriler' },
                    { ad: 'Etiket', aciklama: 'Atanmış etiketlere göre filtrele' },
                    { ad: 'Bakiye', aciklama: 'Alacaklı / Borçlu / Sıfır bakiyeli' },
                ]} />
            </Adim>

            <Gorsel aciklama="Filtre paneli açık — tür, kategori, etiket seçenekleri ve 'Filtrele' butonu" />

            <BaslikH3>Müşteri Bilgilerini Düzenleme</BaslikH3>

            <Adim no={1} baslik="Müşteri kartına gidin">
                Listeden müşterinin adına tıklayın. Müşteri detay sayfası açılır.
            </Adim>

            <Gorsel aciklama="Müşteri detay sayfası — bilgiler, bakiye özeti, sekmeler (Notlar, Dosyalar, Sözleşmeler, Ödemeler)" />

            <Adim no={2} baslik="Düzenle butonuna tıklayın">
                Sayfanın üst kısmındaki <strong>Düzenle</strong> butonuna tıklayın.
                Bilgiler düzenlenebilir hale gelir.
            </Adim>

            <Adim no={3} baslik="Değişiklikleri kaydedin">
                Güncellemelerinizi yapıp <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <BaslikH3>Müşteri Detay Sekmeleri</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: 'Notlar', aciklama: 'Müşteriye özel notlar ve görüşme kayıtları' },
                { ad: 'Dosyalar', aciklama: 'Yüklenen belgeler: sözleşme kopyaları, kimlik vb.' },
                { ad: 'Sözleşmeler', aciklama: 'Bu müşteriyle yapılan tüm sözleşmeler' },
                { ad: 'Ödemeler', aciklama: 'Tahsilat ve ödeme geçmişi, bakiye durumu' },
                { ad: 'İYS İzinleri', aciklama: 'SMS, e-posta ve telefon iletişim izin kayıtları' },
            ]} />

            <Gorsel aciklama="Müşteri detayı — sekmeler vurgulanmış, Ödemeler sekmesi açık ve bakiye özeti görünür" />

            <BaslikH3>Müşteriyi Pasif Yapma</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Artık aktif olmayan müşteriler için <strong>Pasif</strong> işlemi yapılabilir.
                Pasif müşteriler listede görünmez ancak kayıtları silinmez; gerektiğinde tekrar aktif yapılabilir.
            </p>

            <Ipucu tur="uyari">
                Aktif sözleşmesi veya açık bakiyesi olan müşteriler pasif yapılmadan önce bu durumların çözüme kavuşturulması önerilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       TEDARİKÇİLER — Yeni Tedarikçi Ekleme
    ════════════════════════════════════════════════════════════ */
    'tedarikciler/yeni-tedarikci': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Tedarikçiler; müteahhitler, malzeme tedarikçileri ve hizmet sağlayıcılar gibi firmaya mal veya
                hizmet sağlayan kişi ve kuruluşlardır. Müşterilerle aynı <strong>Cari</strong> yapısı kullanılır,
                yalnızca türü <strong>"Tedarikçi"</strong> olarak belirlenir.
            </p>

            <BaslikH3>Yeni Tedarikçi Ekleme</BaslikH3>

            <Adim no={1} baslik="Tedarikçiler sayfasını açın">
                Sol menüden <strong>Tedarikçiler</strong> bağlantısına tıklayın.
                Yalnızca türü "Tedarikçi" olan kayıtlar listelenir.
            </Adim>

            <Gorsel aciklama="Tedarikçiler listesi — firma adı, telefon, bakiye sütunları ve '+ Yeni Tedarikçi' butonu" />

            <Adim no={2} baslik="'+ Yeni Tedarikçi' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Tedarikçi</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Tedarikçi bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Ad Soyad', aciklama: 'Bireysel tedarikçi için ad soyad', zorunlu: true },
                    { ad: 'Firma Adı', aciklama: 'Şirket adı (kurumsal tedarikçiler için)' },
                    { ad: 'Telefon 1', aciklama: 'Ana iletişim numarası', zorunlu: true },
                    { ad: 'Telefon 2', aciklama: 'İkinci telefon numarası' },
                    { ad: 'E-posta', aciklama: 'E-posta adresi' },
                    { ad: 'Adres', aciklama: 'Firma adresi' },
                    { ad: 'Vergi Dairesi', aciklama: 'Vergi dairesi adı' },
                    { ad: 'Vergi No', aciklama: 'Vergi kimlik numarası' },
                    { ad: 'Alt Kategori', aciklama: 'Tedarikçi türü: Müteahhit, Malzeme, Hizmet vb.' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni tedarikçi ekleme formu — firma bilgileri ve vergi alanları dolu hâlde" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın. Tedarikçi oluşturulur ve otomatik <strong>Takip No</strong> atanır.
            </Adim>

            <Ipucu tur="bilgi">
                Tedarikçiye yapılan ödemeler <strong>Finansal İşlemler</strong> modülünden işlenir.
                Ödeme kaydedildiğinde tedarikçi bakiyesi otomatik güncellenir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       TEDARİKÇİLER — Arama, Düzenleme
    ════════════════════════════════════════════════════════════ */
    'tedarikciler/arama-duzenleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Tedarikçi listesinde arama, filtreleme ve düzenleme işlemleri Müşteriler modülüyle
                aynı şekilde çalışır. Tedarikçiye tıklayarak detay sayfasına, oradan da finansal geçmişe ulaşılır.
            </p>

            <BaslikH3>Arama ve Filtreleme</BaslikH3>

            <Adim no={1} baslik="Tedarikçi arayın">
                Listenin üstündeki arama kutusuna firma adı, telefon veya takip numarasını girin.
                Sonuçlar anında filtrelenir.
            </Adim>

            <Gorsel aciklama="Tedarikçiler listesi — arama kutusu aktif, filtrelenmiş sonuçlar" />

            <Adim no={2} baslik="Alt kategoriye göre filtreleyin">
                <strong>Filtrele</strong> panelinden <strong>Alt Kategori</strong> seçerek
                (örn. sadece "Müteahhit" türündeki tedarikçiler) listeyi daraltabilirsiniz.
            </Adim>

            <BaslikH3>Tedarikçi Bilgilerini Düzenleme</BaslikH3>

            <Adim no={1} baslik="Tedarikçi kartını açın">
                Listeden tedarikçinin adına tıklayın. Detay sayfası açılır.
            </Adim>

            <Adim no={2} baslik="Düzenle butonuna tıklayın">
                <strong>Düzenle</strong> butonuna tıklayarak bilgileri güncelleyin ve <strong>Kaydet</strong> basın.
            </Adim>

            <Gorsel aciklama="Tedarikçi detay sayfası — bilgiler, bakiye özeti ve alt sekmeler görünür" />

            <BaslikH3>Tedarikçi Detay Sekmeleri</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: 'Notlar', aciklama: 'Tedarikçiye özel görüşme notları ve hatırlatıcılar' },
                { ad: 'Dosyalar', aciklama: 'Sözleşmeler, teklifler ve diğer belgeler' },
                { ad: 'Ödemeler', aciklama: 'Yapılan ödemeler ve borç geçmişi' },
            ]} />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       TEDARİKÇİLER — Finansal Ekstre
    ════════════════════════════════════════════════════════════ */
    'tedarikciler/finansal-ekstre': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Finansal ekstre; bir tedarikçiye yapılan tüm ödemelerin, borçların ve bakiye hareketlerinin
                kronolojik dökümüdür. Muhasebe takibi ve tedarikçi ile mutabakat için kullanılır.
            </p>

            <BaslikH3>Finansal Ekstreye Erişim</BaslikH3>

            <Adim no={1} baslik="Tedarikçi detayına gidin">
                Tedarikçiler listesinden ilgili tedarikçiye tıklayın.
                Detay sayfasının üst kısmında <strong>mevcut bakiye</strong> görüntülenir.
            </Adim>

            <Gorsel aciklama="Tedarikçi detay sayfası üstü — bakiye kartı (Borç / Alacak / Net Bakiye) büyük rakamlarla gösterilir" />

            <Adim no={2} baslik="Ödemeler sekmesine geçin">
                <strong>Ödemeler</strong> sekmesine tıklayın. Tüm finansal hareketler listelenir.
            </Adim>

            <Gorsel aciklama="Ödemeler sekmesi — tarih, açıklama, tutar, yön (borç/alacak) ve bakiye sütunlarıyla hareketler tablosu" />

            <BaslikH3>Ekstre İçeriği</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: 'Tarih', aciklama: 'İşlemin gerçekleştiği tarih' },
                { ad: 'Açıklama', aciklama: 'Ödemenin tanımı veya fatura bilgisi' },
                { ad: 'Tutar', aciklama: 'İşlem tutarı ve para birimi' },
                { ad: 'Yön', aciklama: '"Giden" (tedarikçiye ödeme) veya "Gelen" (tedarikçiden tahsilat)' },
                { ad: 'Kasa', aciklama: 'Ödemenin yapıldığı kasa veya banka hesabı' },
                { ad: 'Bakiye', aciklama: 'İşlem sonrası anlık bakiye' },
            ]} />

            <BaslikH3>Ekstre Filtreleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Ödemeler sekmesinde tarih aralığı girerek belirli bir dönemin ekstresi görüntülenebilir.
            </p>

            <Gorsel aciklama="Ödemeler sekmesi — tarih aralığı filtresi açık, 'Başlangıç' ve 'Bitiş' tarih seçicileri" />

            <BaslikH3>Ekstre Yazdırma / Dışa Aktarma</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Hareketler listesinin üstündeki <strong>PDF İndir</strong> veya <strong>Excel'e Aktar</strong>
                butonuyla ekstre dışa aktarılabilir. Tedarikçi ile mutabakat görüşmelerinde kullanılabilir.
            </p>

            <Gorsel aciklama="Ödemeler sekmesi — 'PDF İndir' ve 'Excel'e Aktar' butonları vurgulanmış" />

            <Ipucu tur="bilgi">
                Tedarikçi ekstresi aynı zamanda <strong>Finansal İşlemler &rsaquo; Raporlar</strong> bölümünden
                de alınabilir; burada birden fazla tedarikçiyi karşılaştırmalı görüntüleyebilirsiniz.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SÖZLEŞMELER — Yeni Sözleşme Oluşturma
    ════════════════════════════════════════════════════════════ */
    'sozlesmeler/yeni-sozlesme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Sözleşme modülü; satış sözleşmesi, ön sözleşme, rezervasyon gibi belgelerin oluşturulup
                yönetildiği merkezi alandır. Bir daire satışı kesinleştiğinde buradan sözleşme açılır.
            </p>

            <BaslikH3>Yeni Sözleşme Oluşturma</BaslikH3>

            <Adim no={1} baslik="Sözleşmeler sayfasını açın">
                Sol menüden <strong>Sözleşmeler</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Sözleşme' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Sözleşme</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Sözleşmeler listesi — '+ Yeni Sözleşme' butonu sağ üstte vurgulanmış" />

            <Adim no={3} baslik="Sözleşme bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Sözleşme Türü', aciklama: 'Satış Sözleşmesi, Ön Sözleşme, Rezervasyon vb.', zorunlu: true },
                    { ad: 'Daire', aciklama: 'Hangi daireye ait olduğu — proje ve daire seçilir', zorunlu: true },
                    { ad: 'Müşteri', aciklama: 'Sözleşme yapılan müşteri (Müşteriler listesinden)', zorunlu: true },
                    { ad: 'Satış Fiyatı', aciklama: 'Toplam satış bedeli ve para birimi', zorunlu: true },
                    { ad: 'Sözleşme Tarihi', aciklama: 'Sözleşmenin imzalandığı tarih', zorunlu: true },
                    { ad: 'Teslim Tarihi', aciklama: 'Dairenin teslim edileceği tarih' },
                    { ad: 'Temsilci', aciklama: 'Satışı gerçekleştiren kullanıcı' },
                    { ad: 'Notlar', aciklama: 'Sözleşmeyle ilgili ek açıklamalar' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni sözleşme formu — daire ve müşteri seçimi, satış fiyatı, tarih alanları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna tıklayın. Sözleşme oluşturulur, dairenin durumu otomatik olarak
                <strong> Satıldı</strong> olarak güncellenir.
            </Adim>

            <Ipucu tur="bilgi">
                Bir müşteri için birden fazla daire satışı yapılabilir; her daire için ayrı sözleşme oluşturulur.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SÖZLEŞMELER — Hazır Ödeme Planları Oluşturma
    ════════════════════════════════════════════════════════════ */
    'sozlesmeler/odeme-planlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Ödeme planı, sözleşme tutarının vadelere bölünmüş halidir. Hazır şablonlar kullanılarak
                otomatik ödeme planı oluşturulabilir veya taksitler manuel olarak girilir.
            </p>

            <BaslikH3>Şablondan Ödeme Planı Oluşturma</BaslikH3>

            <Adim no={1} baslik="Sözleşme detayına gidin">
                Sözleşmeler listesinden ilgili sözleşmeye tıklayın.
            </Adim>

            <Adim no={2} baslik="Ödeme Planı sekmesine geçin">
                <strong>Ödeme Planı</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={3} baslik="Şablon seçin">
                <strong>Şablondan Yükle</strong> butonuna tıklayın.
                Sistem Ayarları'nda tanımlı hazır şablonlar listelenir.
                İstediğiniz şablonu seçin.
            </Adim>

            <Gorsel aciklama="Ödeme planı sekmesi — 'Şablondan Yükle' butonuna tıklandığında şablon listesi açılmış" />

            <Adim no={4} baslik="Planı onaylayın">
                Şablon seçildikten sonra taksit tutarları ve tarihler otomatik hesaplanır.
                Gerekirse tutarları veya tarihleri düzenleyin ve <strong>Kaydet</strong> basın.
            </Adim>

            <Gorsel aciklama="Şablondan oluşturulan ödeme planı — taksit no, tarih, tutar sütunları ve toplam özet" />

            <BaslikH3>Manuel Taksit Ekleme</BaslikH3>

            <Adim no={1} baslik="+ Taksit Ekle">
                <strong>+ Taksit Ekle</strong> butonuyla bireysel taksit satırı ekleyin.
            </Adim>
            <Adim no={2} baslik="Taksit bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Vade Tarihi', aciklama: 'Taksitin ödeneceği tarih', zorunlu: true },
                    { ad: 'Tutar', aciklama: 'Taksit tutarı', zorunlu: true },
                    { ad: 'Açıklama', aciklama: 'Ön ödeme, kaparo, teslimde vb.' },
                ]} />
            </Adim>

            <Ipucu tur="bilgi">
                Taksit tutarlarının toplamı sözleşme satış fiyatına eşit olmak zorunda değildir;
                ancak fark oluşması hâlinde sistem uyarı gösterir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SÖZLEŞMELER — Sözleşme Dokümanları
    ════════════════════════════════════════════════════════════ */
    'sozlesmeler/dokumanlar': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Sözleşme dokümanları sekmesi; imzalı sözleşme kopyası, kimlik fotokopisi, tapu belgesi gibi
                dosyaların sözleşme kaydına bağlanmasını sağlar.
            </p>

            <BaslikH3>Dosya Yükleme</BaslikH3>

            <Adim no={1} baslik="Dokümanlar sekmesine geçin">
                Sözleşme detayında <strong>Dokümanlar</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="Dosya yükleyin">
                <strong>+ Dosya Ekle</strong> butonuna tıklayın. Bilgisayarınızdan dosya seçin.
                PDF, JPG, PNG ve Word formatları desteklenir.
            </Adim>

            <Gorsel aciklama="Dokümanlar sekmesi — yüklü dosyalar listesi, her satırda dosya adı, boyut ve indirme butonu" />

            <Adim no={3} baslik="Dosyayı kategorilendirin">
                Yüklenen dosyaya bir kategori etiket ekleyebilirsiniz:
                <strong> Kimlik, Sözleşme, Tapu, Diğer</strong> vb.
            </Adim>

            <Ipucu tur="bilgi">
                Sistem Ayarları &rsaquo; Döküman Şablonları bölümünde oluşturulan şablonlar kullanılarak
                Word/PDF formatında sözleşme belgesi otomatik oluşturulabilir.
                Bkz. <strong>Sözleşme İndir</strong> makalesi.
            </Ipucu>

            <BaslikH3>Dosya İndirme ve Silme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Dosya listesinde her satırda <strong>İndir</strong> ve <strong>Sil</strong> butonları bulunur.
                Silinen dosyalar geri getirilemez.
            </p>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SÖZLEŞMELER — İmalat Notları
    ════════════════════════════════════════════════════════════ */
    'sozlesmeler/imalat-notlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                İmalat notları; müşterinin dairesi için talep ettiği özel imalat değişikliklerini
                (zemin kaplaması, mutfak malzemesi, renk seçimleri vb.) kayıt altına alır.
                Bu notlar sözleşmeye ek olarak indirilecek belgede de yer alabilir.
            </p>

            <BaslikH3>İmalat Notu Ekleme</BaslikH3>

            <Adim no={1} baslik="İmalat Notları sekmesine geçin">
                Sözleşme detayında <strong>İmalat Notları</strong> sekmesine tıklayın.
            </Adim>

            <Gorsel aciklama="İmalat Notları sekmesi — not listesi ve '+ Yeni Not' butonu" />

            <Adim no={2} baslik="Yeni Not Ekle butonuna tıklayın">
                <strong>+ Yeni Not</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Not bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Not kategorisi: Zemin, Mutfak, Banyo, Elektrik vb.', zorunlu: true },
                    { ad: 'Açıklama', aciklama: 'Müşterinin talebinin detaylı açıklaması' },
                    { ad: 'Durum', aciklama: 'Talep Alındı, İnceleniyor, Onaylandı, Reddedildi' },
                ]} />
            </Adim>

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Gorsel aciklama="İmalat notu detayı — başlık, açıklama, durum bilgisi ve düzenle/sil butonları" />

            <Ipucu tur="bilgi">
                İmalat notları, sözleşme belgesi şablonuna <code>&#123;&#123;imalatNotlari&#125;&#125;</code> etiketi eklenerek
                sözleşme PDF'ine otomatik dahil edilebilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SÖZLEŞMELER — Sözleşme İndir
    ════════════════════════════════════════════════════════════ */
    'sozlesmeler/indir': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Sözleşme belgesi, Sistem Ayarları'nda tanımlı şablonlar kullanılarak otomatik
                doldurulup PDF veya Word olarak indirilebilir.
            </p>

            <BaslikH3>Sözleşme Belgesi İndirme</BaslikH3>

            <Adim no={1} baslik="Sözleşme detayına gidin">
                Sözleşmeler listesinden ilgili sözleşmeye tıklayın.
            </Adim>

            <Adim no={2} baslik="'Sözleşme İndir' butonuna tıklayın">
                Sayfanın üst kısmındaki <strong>Sözleşme İndir</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Sözleşme detayı — üst kısımda 'Sözleşme İndir' butonu vurgulanmış" />

            <Adim no={3} baslik="Şablon seçin">
                Tanımlı birden fazla şablon varsa hangisini kullanmak istediğinizi seçin.
            </Adim>

            <Adim no={4} baslik="İndirin">
                Sistem, şablondaki değişkenleri (müşteri adı, daire no, tutar, tarih vb.) otomatik doldurar
                dosyayı hazırlar. PDF veya Word formatında bilgisayarınıza kaydedilir.
            </Adim>

            <Gorsel aciklama="Şablon seçim ekranı — mevcut şablonlar listelenir, format seçimi (PDF/Word) ve İndir butonu" />

            <Ipucu tur="bilgi">
                Şablonları düzenlemek için <strong>Sistem Ayarları &rsaquo; Döküman Şablonları</strong> bölümüne gidin.
                Şablon içinde <code>&#123;&#123;musteriAdi&#125;&#125;</code>, <code>&#123;&#123;daireNo&#125;&#125;</code>, <code>&#123;&#123;satisFiyati&#125;&#125;</code> gibi
                değişkenler kullanılabilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       EMLAK — Portföy Ekleme
    ════════════════════════════════════════════════════════════ */
    'emlak/ekleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Emlak Portföy modülü; satılık veya kiralık bireysel mülkleri (daire, villa, arsa, dükkan vb.)
                sisteme eklemenizi ve takip etmenizi sağlar. Projeler modülüyle bağlantısı yoktur.
            </p>

            <BaslikH3>Yeni Portföy Ekleme</BaslikH3>

            <Adim no={1} baslik="Emlak Portföy sayfasını açın">
                Sol menüden <strong>Emlak &rsaquo; Portföy</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Portföy' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Portföy</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Emlak portföy listesi — '+ Yeni Portföy' butonu vurgulanmış, mevcut portföyler sıralanmış" />

            <Adim no={3} baslik="Portföy bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Portföy adı veya adresi', zorunlu: true },
                    { ad: 'Tür', aciklama: 'Daire, Villa, Arsa, Dükkan, Ofis vb.', zorunlu: true },
                    { ad: 'Durum', aciklama: 'Satılık, Kiralık, Satıldı, Kiralandı' },
                    { ad: 'Fiyat', aciklama: 'Satış veya kira fiyatı ve para birimi' },
                    { ad: 'Adres', aciklama: 'İl, ilçe, mahalle, açık adres' },
                    { ad: 'Brüt m²', aciklama: 'Brüt kullanım alanı' },
                    { ad: 'Net m²', aciklama: 'Net kullanım alanı' },
                    { ad: 'Oda Sayısı', aciklama: '1+1, 2+1, 3+1 vb.' },
                    { ad: 'Kat', aciklama: 'Mülkün bulunduğu kat' },
                    { ad: 'Açıklama', aciklama: 'Mülk hakkında detaylı bilgi ve özellikler' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni portföy formu — mülk türü, fiyat, adres ve alan bilgileri alanları dolu" />

            <Adim no={4} baslik="Fotoğraf ekleyin">
                Formu kaydettikten sonra <strong>Görseller</strong> sekmesinden mülke ait fotoğrafları yükleyin.
                İlk fotoğraf otomatik olarak kapak görseli olur.
            </Adim>

            <Adim no={5} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="bilgi">
                Portföy oluşturduktan sonra <strong>Sunum & Teklif</strong> özelliğiyle
                mülkleri seçili müşterilere sunum paketi hâlinde gönderebilirsiniz.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       EMLAK — Müşteri Sunumları
    ════════════════════════════════════════════════════════════ */
    'emlak/musteri-sunumlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Emlak portföyündeki mülkler müşteriye özel sunum paketlerine eklenebilir.
                Müşteri, paylaşılan link üzerinden mülk detaylarını ve fotoğraflarını inceleyebilir.
            </p>

            <BaslikH3>Sunum Oluşturma</BaslikH3>

            <Adim no={1} baslik="Emlak Sunumları sayfasını açın">
                Sol menüden <strong>Emlak &rsaquo; Sunumlar</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Sunum' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Sunum</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Sunum bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Sunum adı', zorunlu: true },
                    { ad: 'Müşteri', aciklama: 'Sunumun yapılacağı müşteri', zorunlu: true },
                    { ad: 'Notlar', aciklama: 'Sunum hakkında ek açıklama' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni emlak sunumu formu — başlık ve müşteri seçimi" />

            <Adim no={4} baslik="Portföyleri ekleyin">
                Sunum oluşturduktan sonra detay sayfasından <strong>+ Portföy Ekle</strong> butonuyla
                mülkleri sunuma ekleyin.
            </Adim>

            <Adim no={5} baslik="Paylaşın">
                <strong>Paylaş</strong> butonuyla müşteriye özel link oluşturun ve müşteriyle paylaşın.
            </Adim>

            <Gorsel aciklama="Sunum detayı — eklenen mülkler listesi ve 'Paylaş' butonu" />

            <Ipucu tur="bilgi">
                Müşterinin etkileşimleri (görüntüleme, beğeni, red) sunum akışında kaydedilir.
                Bu veriler takip görüşmelerinde rehber olarak kullanılabilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KİRALIK GAYRİMENKULLER — Ekleme
    ════════════════════════════════════════════════════════════ */
    'kiralik/ekleme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Kiralık Gayrimenkuller modülü; yönetilen veya kiralanan mülklerin takibini sağlar.
                Her mülke kira sözleşmeleri, kiracılar ve ödeme takipleri bağlanır.
            </p>

            <BaslikH3>Yeni Gayrimenkul Ekleme</BaslikH3>

            <Adim no={1} baslik="Kiralık Portföy sayfasını açın">
                Sol menüden <strong>Kiralık &rsaquo; Portföy</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Gayrimenkul' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Gayrimenkul</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Kiralık portföy listesi — mülk adı, tür, durum ve aktif kira sözleşmesi sayısı sütunları" />

            <Adim no={3} baslik="Gayrimenkul bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Başlık', aciklama: 'Mülkün tanımlayıcı adı', zorunlu: true },
                    { ad: 'Tür', aciklama: 'Daire, Dükkan, Depo, Ofis vb.', zorunlu: true },
                    { ad: 'Adres', aciklama: 'Tam adres bilgisi' },
                    { ad: 'Net m²', aciklama: 'Kullanım alanı' },
                    { ad: 'Kira Bedeli', aciklama: 'Aylık tahmini kira bedeli (bilgi amaçlı)' },
                    { ad: 'Notlar', aciklama: 'Mülkle ilgili özel notlar' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni gayrimenkul formu — başlık, tür ve adres alanları dolu" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="bilgi">
                Gayrimenkul eklendikten sonra <strong>Kira Sözleşmesi</strong> oluşturarak
                kiracı bilgilerini ve ödeme planını bağlayabilirsiniz.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KİRALIK GAYRİMENKULLER — Kira Sözleşmesi
    ════════════════════════════════════════════════════════════ */
    'kiralik/sozlesme': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Kira sözleşmesi; gayrimenkul, kiracı, kira dönemi ve ödeme planını bir arada tutar.
                Kira ödeme tarihleri yaklaştığında sistem hatırlatma oluşturur.
            </p>

            <BaslikH3>Yeni Kira Sözleşmesi Oluşturma</BaslikH3>

            <Adim no={1} baslik="Kira Sözleşmeleri sayfasını açın">
                Sol menüden <strong>Kiralık &rsaquo; Sözleşmeler</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Sözleşme' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Sözleşme</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Sözleşme bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Gayrimenkul', aciklama: 'Kiralanacak mülk portföyden seçilir', zorunlu: true },
                    { ad: 'Kiracı', aciklama: 'Müşteriler/Cariler listesinden kiracı seçilir', zorunlu: true },
                    { ad: 'Başlangıç Tarihi', aciklama: 'Kira sözleşmesinin başlangıç tarihi', zorunlu: true },
                    { ad: 'Bitiş Tarihi', aciklama: 'Kira sözleşmesinin bitiş tarihi' },
                    { ad: 'Aylık Kira', aciklama: 'Aylık kira bedeli', zorunlu: true },
                    { ad: 'Ödeme Günü', aciklama: 'Her ay kiranın ödeneceği gün (örn. her ayın 1\'i)' },
                    { ad: 'Depozito', aciklama: 'Depozito tutarı' },
                    { ad: 'Artış Oranı', aciklama: 'Yıllık kira artış oranı (%)' },
                ]} />
            </Adim>

            <Gorsel aciklama="Kira sözleşmesi formu — gayrimenkul, kiracı, tarih aralığı ve aylık kira alanları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna tıklayın.
                Sistem kira ödeme planını otomatik oluşturur.
            </Adim>

            <BaslikH3>Kira Ödemelerini Takip Etme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Sözleşme detayındaki <strong>Ödemeler</strong> sekmesinde her ayın kira durumu görüntülenir.
                Ödeme alındığında ilgili satırı <strong>Ödendi</strong> olarak işaretleyin.
            </p>

            <Gorsel aciklama="Kira ödemeleri listesi — ay, tutar, durum (Ödendi/Bekliyor/Gecikmiş) ve işaretleme butonu" />

            <Ipucu tur="uyari">
                Sözleşme bitiş tarihi dolduğunda sistem otomatik olarak sözleşmeyi <strong>Süresi Doldu</strong> olarak işaretler.
                Yenileme gerekiyorsa yeni bir sözleşme oluşturun.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Yeni Kat Karşılığı
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/yeni': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Kat Karşılığı modülü; arsa sahipleri ile müteahhitler arasındaki kat karşılığı inşaat
                projelerini yönetir. Arsa ortakları, müteahhitler, paylaşım oranları ve blok/kat yapısı
                bu modülde tanımlanır.
            </p>

            <BaslikH3>Yeni Kat Karşılığı Projesi Oluşturma</BaslikH3>

            <Adim no={1} baslik="Kat Karşılığı sayfasını açın">
                Sol menüden <strong>Kat Karşılığı</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Proje' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Proje</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Kat karşılığı proje listesi — proje adı, arsa adresi, toplam bağımsız bölüm sayısı sütunları" />

            <Adim no={3} baslik="Proje bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Proje Adı', aciklama: 'Projenin tanımlayıcı adı', zorunlu: true },
                    { ad: 'Arsa Adresi', aciklama: 'Projenin bulunduğu arsa adresi' },
                    { ad: 'Tapu Bilgisi', aciklama: 'Ada, parsel no' },
                    { ad: 'Toplam m²', aciklama: 'Arsa toplam alanı' },
                    { ad: 'Notlar', aciklama: 'Proje hakkında ek açıklamalar' },
                ]} />
            </Adim>

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın. Proje oluşturulur; ardından
                <strong> Bölümler, Müteahhitler, Arsa Ortakları</strong> gibi sekmeleri doldurun.
            </Adim>

            <Gorsel aciklama="Yeni proje kaydedildi — proje detay sayfası, sekmeler vurgulanmış" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Bölümler
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/bolumler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Bölümler sekmesi, projedeki bağımsız bölümlerin (daire, dükkan, ofis) listesini gösterir.
                Her bölüm için kime (arsa ortağı veya müteahhit) düştüğü ve durumu takip edilir.
            </p>

            <BaslikH3>Bölüm Listesi</BaslikH3>

            <Adim no={1} baslik="Bölümler sekmesine geçin">
                Kat karşılığı proje detayında <strong>Bölümler</strong> sekmesine tıklayın.
            </Adim>

            <Gorsel aciklama="Bölümler sekmesi — bölüm no, tür, blok/kat, düşen taraf (arsa/müteahhit), durum sütunları" />

            <Adim no={2} baslik="Bölüm ekleyin">
                <strong>+ Bölüm Ekle</strong> butonuyla tek tek bölüm eklenebilir;
                blok ve kat yapısı tanımlandıktan sonra <strong>Otomatik Oluştur</strong> seçeneğiyle
                bölümler toplu oluşturulabilir.
            </Adim>

            <AlanTablosu alanlar={[
                { ad: 'Bölüm No', aciklama: 'Tapu veya proje numarası', zorunlu: true },
                { ad: 'Tür', aciklama: 'Daire, Dükkan, Depo, Ofis vb.' },
                { ad: 'Blok / Kat', aciklama: 'Bölümün bulunduğu blok ve kat' },
                { ad: 'Brüt m²', aciklama: 'Brüt alan' },
                { ad: 'Düşen Taraf', aciklama: 'Arsa ortağına mı müteahhite mi düştüğü' },
                { ad: 'Durum', aciklama: 'Boş, Satışa Açıldı, Satıldı' },
            ]} />

            <Gorsel aciklama="Bölüm ekleme formu — bölüm no, tür, blok/kat ve düşen taraf alanları" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Genel Bilgiler
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/genel-bilgiler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Genel Bilgiler sekmesi; proje adı, arsa bilgileri ve tapu durumu gibi temel
                proje verilerini görüntüler ve düzenlemenizi sağlar.
            </p>

            <BaslikH3>Genel Bilgileri Düzenleme</BaslikH3>

            <Adim no={1} baslik="Genel Bilgiler sekmesine geçin">
                Proje detayında <strong>Genel Bilgiler</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="Düzenle butonuna tıklayın">
                <strong>Düzenle</strong> butonuna tıklayarak alanları güncelleyin.
            </Adim>

            <AlanTablosu alanlar={[
                { ad: 'Proje Adı', aciklama: 'Projenin adı' },
                { ad: 'Arsa Adresi', aciklama: 'Projenin bulunduğu yer' },
                { ad: 'Ada / Parsel', aciklama: 'Tapu kayıt bilgileri' },
                { ad: 'Toplam Arsa m²', aciklama: 'Arsa alanı' },
                { ad: 'İnşaat Ruhsatı No', aciklama: 'Ruhsat numarası ve tarihi' },
                { ad: 'Proje Durumu', aciklama: 'Planlama, İnşaat, Tamamlandı vb.' },
                { ad: 'Tahmini Teslim', aciklama: 'Teslim tarihi tahmini' },
            ]} />

            <Gorsel aciklama="Genel Bilgiler sekmesi — arsa ve ruhsat bilgileri düzenleme modunda" />

            <Adim no={3} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Müteahhitler
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/muteahhitler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Müteahhitler sekmesi, projeyi yürüten müteahhit firma veya kişilerin projeye bağlanmasını
                ve paylaşım oranlarının tanımlanmasını sağlar.
            </p>

            <BaslikH3>Müteahhit Ekleme</BaslikH3>

            <Adim no={1} baslik="Müteahhitler sekmesine geçin">
                Proje detayında <strong>Müteahhitler</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Müteahhit Ekle' butonuna tıklayın">
                <strong>+ Müteahhit Ekle</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Müteahhiti seçin">
                Tedarikçiler listesinden müteahhit firmayı seçin.
                Listede yoksa önce <strong>Tedarikçiler</strong> modülünden ekleyin.
            </Adim>

            <Adim no={4} baslik="Paylaşım oranını girin">
                <AlanTablosu alanlar={[
                    { ad: 'Müteahhit', aciklama: 'Tedarikçiler listesinden seçilir', zorunlu: true },
                    { ad: 'Pay Oranı (%)', aciklama: 'Müteahhidin projeden aldığı pay yüzdesi', zorunlu: true },
                    { ad: 'Notlar', aciklama: 'Anlaşma şartları veya ek açıklamalar' },
                ]} />
            </Adim>

            <Gorsel aciklama="Müteahhitler sekmesi — müteahhit adı ve pay oranı sütunları, toplam kontrol satırı" />

            <Ipucu tur="uyari">
                Tüm müteahhit ve arsa ortaklarının pay toplamının <strong>%100</strong> olması gerekir.
                Sistem bu kontrolü otomatik yapar.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Arsa Ortakları
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/arsa-ortaklari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Arsa Ortakları sekmesi, arsayı sağlayan kişi veya kurumların projeye eklenmesini
                ve her ortağın pay oranının belirlenmesini sağlar.
            </p>

            <BaslikH3>Arsa Ortağı Ekleme</BaslikH3>

            <Adim no={1} baslik="Arsa Ortakları sekmesine geçin">
                Proje detayında <strong>Arsa Ortakları</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Ortak Ekle' butonuna tıklayın">
                <strong>+ Ortak Ekle</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Ortak bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Ortak', aciklama: 'Müşteriler listesinden arsa sahibi seçilir', zorunlu: true },
                    { ad: 'Pay Oranı (%)', aciklama: 'Bu ortağın arsa payı yüzdesi', zorunlu: true },
                    { ad: 'TC Kimlik No', aciklama: 'Tapu işlemleri için kimlik bilgisi' },
                    { ad: 'Tapu Hissesi', aciklama: 'Resmi tapu hissesi (örn. 1/4, 3/8)' },
                ]} />
            </Adim>

            <Gorsel aciklama="Arsa ortakları sekmesi — ortak adı, pay oranı ve tapu hissesi bilgileri" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
                Tüm ortakların payları toplamda <strong>%100</strong> etmelidir.
            </Adim>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Bloklar & Katlar
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/bloklar-katlar': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Bloklar ve Katlar sekmesinde proje binaları ve kat yapısı tanımlanır.
                Bu bilgiler, bölümlerin otomatik oluşturulmasında ve görünümlerde kullanılır.
            </p>

            <BaslikH3>Blok Ekleme</BaslikH3>

            <Adim no={1} baslik="Bloklar & Katlar sekmesine geçin">
                Proje detayında <strong>Bloklar & Katlar</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Blok Ekle' butonuna tıklayın">
                <strong>+ Blok Ekle</strong> butonuna tıklayın ve blok adını girin (A Blok, B Blok vb.).
            </Adim>

            <Gorsel aciklama="Bloklar listesi — A Blok, B Blok kartları, her kart içinde kat sayısı ve bölüm sayısı" />

            <BaslikH3>Kat Ekleme</BaslikH3>

            <Adim no={1} baslik="Bloğu genişletin">
                İlgili bloğun kartına tıklayın veya genişlet okuna basın.
            </Adim>

            <Adim no={2} baslik="'+ Kat Ekle' butonuna tıklayın">
                <strong>+ Kat Ekle</strong> butonuyla kata sıra numarası ve adı girin
                (Zemin Kat, 1. Kat, 2. Kat vb.).
            </Adim>

            <AlanTablosu alanlar={[
                { ad: 'Sıra', aciklama: 'Katın sıra numarası (yerden yukarıya doğru)', zorunlu: true },
                { ad: 'Ad', aciklama: 'Zemin Kat, 1. Kat, Bodrum Kat vb.', zorunlu: true },
                { ad: 'Bölüm Sayısı', aciklama: 'Bu kattaki bağımsız bölüm sayısı' },
            ]} />

            <Gorsel aciklama="Kat listesi — A Blok altında sıralı katlar, her satırda bölüm sayısı" />

            <Ipucu tur="bilgi">
                Blok ve kat yapısı tanımlandıktan sonra <strong>Bölümler</strong> sekmesinden
                <strong> Otomatik Bölüm Oluştur</strong> seçeneğiyle tüm bölümler toplu olarak oluşturulabilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Cepheler
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/cepheler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Cepheler sekmesi, projedeki bağımsız bölümlerin hangi yöne baktığını tanımlar.
                Kuzey, Güney, Doğu, Batı veya özel cephe isimleri kullanılabilir.
            </p>

            <BaslikH3>Cephe Tanımlama</BaslikH3>

            <Adim no={1} baslik="Cepheler sekmesine geçin">
                Proje detayında <strong>Cepheler</strong> sekmesine tıklayın.
            </Adim>

            <Adim no={2} baslik="Cepheleri gözden geçirin">
                Sistem varsayılan cepheleri otomatik listeler (Kuzey, Güney, Doğu, Batı).
                Projeye özgü eklemeler için <strong>+ Cephe Ekle</strong> butonunu kullanın.
            </Adim>

            <Gorsel aciklama="Cepheler sekmesi — cephe adları listesi ve her cepheye atanmış bölüm sayısı" />

            <Adim no={3} baslik="Bölümlere cephe atayın">
                <strong>Bölümler</strong> sekmesine geçip her bölümün cephe alanını doldurun,
                ya da toplu güncelleme aracını kullanın.
            </Adim>

            <Ipucu tur="bilgi">
                Cephe bilgisi, proje görünümlerinde bölümleri cepheye göre filtrelemek için kullanılır.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       KAT KARŞILIĞI — Paylaşımlar
    ════════════════════════════════════════════════════════════ */
    'kat-karsiligi/paylasimlar': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Paylaşımlar sekmesi, bağımsız bölümlerin arsa ortakları ile müteahhit arasındaki
                dağılımını gösterir ve paylaşım dengesini kontrol etmenizi sağlar.
            </p>

            <BaslikH3>Paylaşım Görünümü</BaslikH3>

            <Adim no={1} baslik="Paylaşımlar sekmesine geçin">
                Proje detayında <strong>Paylaşımlar</strong> sekmesine tıklayın.
            </Adim>

            <Gorsel aciklama="Paylaşımlar sekmesi — sol sütunda arsa ortakları, sağda müteahhit, ortada bölüm dağılım tablosu" />

            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Her bölüm için <strong>kime düştüğü</strong> (hangi arsa ortağı veya müteahhit) görüntülenir.
                Toplamda her tarafın kaç bölüm aldığı ve bu bölümlerin toplam m² değeri özetlenir.
            </p>

            <BaslikH3>Paylaşım Düzenleme</BaslikH3>

            <Adim no={1} baslik="Bölüm satırından düzenleyin">
                Herhangi bir bölümün <strong>Düşen Taraf</strong> alanını değiştirmek için
                <strong> Bölümler</strong> sekmesine gidin ve ilgili satırı düzenleyin.
            </Adim>

            <Gorsel aciklama="Bölüm düzenleme formu — 'Düşen Taraf' dropdown menüsü: Arsa Ortağı 1, Arsa Ortağı 2, Müteahhit seçenekleri" />

            <Ipucu tur="uyari">
                Paylaşım değiştirildiğinde her iki tarafın toplam pay dengesi otomatik güncellenir.
                Hedef pay oranlarından sapma varsa sistem uyarı gösterir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       FİNANSAL İŞLEMLER — Yeni İşlem
    ════════════════════════════════════════════════════════════ */
    'finansal/yeni-islem': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Finansal İşlemler modülü; tahsilat, ödeme, virman ve diğer kasa hareketlerinin
                kayıt altına alındığı merkezi alandır.
            </p>

            <BaslikH3>Yeni İşlem Oluşturma</BaslikH3>

            <Adim no={1} baslik="Finansal İşlemler sayfasını açın">
                Sol menüden <strong>Finansal İşlemler</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni İşlem' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni İşlem</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="Finansal işlemler listesi — tarih, tür, cari, tutar, kasa sütunları ve '+ Yeni İşlem' butonu" />

            <Adim no={3} baslik="İşlem bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'İşlem Türü', aciklama: 'Tahsilat, Ödeme, Virman, Gider, Gelir', zorunlu: true },
                    { ad: 'Tarih', aciklama: 'İşlem tarihi', zorunlu: true },
                    { ad: 'Tutar', aciklama: 'İşlem tutarı', zorunlu: true },
                    { ad: 'Para Birimi', aciklama: 'TRY, USD, EUR vb.' },
                    { ad: 'Kasa', aciklama: 'Paranın çıktığı veya girdiği kasa/banka hesabı', zorunlu: true },
                    { ad: 'Cari', aciklama: 'İlgili müşteri veya tedarikçi' },
                    { ad: 'Sözleşme', aciklama: 'İşlemi bağlamak istediğiniz sözleşme' },
                    { ad: 'Taksit', aciklama: 'Belirli bir taksit kaydı olarak işlenecekse' },
                    { ad: 'Kategori', aciklama: 'Finansal kategori (muhasebesel sınıflandırma)' },
                    { ad: 'Açıklama', aciklama: 'İşlemin açıklama notu' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni işlem formu — işlem türü, kasa, cari ve tutar alanları dolu" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın. Kasa bakiyesi otomatik güncellenir.
            </Adim>

            <Ipucu tur="bilgi">
                <strong>Tahsilat</strong> kaydederken ilgili sözleşme ve taksiti seçerseniz
                taksit otomatik olarak <strong>Ödendi</strong> işaretlenir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       FİNANSAL İŞLEMLER — Finansal Özet
    ════════════════════════════════════════════════════════════ */
    'finansal/ozet': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Finansal Özet, tüm kasaların ve mali durumun anlık görüntüsünü sunar.
                Gelir-gider dengesi, kasa bazlı bakiyeler ve dönemsel karşılaştırmalar bu ekranda yer alır.
            </p>

            <BaslikH3>Finansal Özet Sayfasına Erişim</BaslikH3>

            <Adim no={1} baslik="Finansal Özet sayfasını açın">
                Sol menüden <strong>Finansal İşlemler &rsaquo; Finansal Özet</strong> bağlantısına tıklayın.
            </Adim>

            <Gorsel aciklama="Finansal özet ana ekranı — kasa kartları (her kasanın bakiyesi), gelir/gider özet grafikleri" />

            <BaslikH3>Özet Kartları</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: 'Toplam Kasa', aciklama: 'Tüm kasaların toplam bakiyesi' },
                { ad: 'Bu Ay Tahsilat', aciklama: 'Cari ayda gerçekleşen toplam tahsilat' },
                { ad: 'Bu Ay Ödeme', aciklama: 'Cari ayda yapılan toplam ödemeler' },
                { ad: 'Bekleyen Taksitler', aciklama: 'Vadesi gelmiş ödenmemiş taksit tutarı' },
                { ad: 'Bu Ay Vadeli', aciklama: 'Bu ay vadesi dolacak taksitler' },
            ]} />

            <Gorsel aciklama="Özet kartları sırası — mavi Toplam Kasa, yeşil Tahsilat, kırmızı Ödeme, sarı Bekleyen kartlar" />

            <BaslikH3>Kasa Bazlı Görünüm</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Her kasa için ayrı bakiye kartı görüntülenir. Bir kasa kartına tıklayarak
                o kasanın ayrıntılı hareket geçmişine geçebilirsiniz.
            </p>

            <Gorsel aciklama="Kasa kartları — her kasa için bakiye, son hareket tarihi ve 'Hareketleri Gör' linki" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       FİNANSAL İŞLEMLER — Taksit Takip
    ════════════════════════════════════════════════════════════ */
    'finansal/taksit-takip': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Taksit Takip paneli; tüm sözleşmelerin ödeme planlarını, vadelere göre sıralı biçimde
                tek ekranda gösterir. Gecikmiş, bugün vadeli ve bu ay vadeli taksitler kolayca görülür.
            </p>

            <BaslikH3>Taksit Takip Panelini Kullanma</BaslikH3>

            <Adim no={1} baslik="Taksit Takip sayfasını açın">
                Sol menüden <strong>Finansal İşlemler &rsaquo; Taksit Takip</strong> bağlantısına tıklayın.
            </Adim>

            <Gorsel aciklama="Taksit takip paneli — üstte filtre seçenekleri, altta taksit listesi: müşteri, daire, tutar, vade, durum sütunları" />

            <BaslikH3>Filtreleme Seçenekleri</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: 'Gecikmiş', aciklama: 'Vadesi geçmiş ödenmemiş taksitler (kırmızı)' },
                { ad: 'Bugün', aciklama: 'Bugün vadesi dolan taksitler (turuncu)' },
                { ad: 'Bu Hafta', aciklama: 'Bu hafta vadeli taksitler' },
                { ad: 'Bu Ay', aciklama: 'Bu ay içinde vadeli taksitler (sarı)' },
                { ad: 'Tüm Bekleyenler', aciklama: 'Henüz ödenmemiş tüm taksitler' },
            ]} />

            <Gorsel aciklama="Filtre butonları aktif — 'Gecikmiş' seçili, liste sadece gecikmiş taksitleri gösteriyor" />

            <BaslikH3>Toplu Tahsilat</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Birden fazla taksiti seçip <strong>Seçilenleri Tahsil Et</strong> butonuyla toplu tahsilat
                kaydı oluşturabilirsiniz.
            </p>

            <Gorsel aciklama="Taksit listesi — checkbox ile seçili satırlar, altta 'Seçilenleri Tahsil Et' butonu" />

            <Ipucu tur="bilgi">
                Taksit listesinde müşteri adına tıklayarak doğrudan müşteri kartına gidebilirsiniz.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       FİNANSAL İŞLEMLER — Raporlar
    ════════════════════════════════════════════════════════════ */
    'finansal/raporlar': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Finansal Raporlar; tahsilat performansı, nakit akışı, hesap hareketleri gibi
                analitik raporları sunur. Raporlar tarih aralığına ve projeye göre filtrelenebilir.
            </p>

            <BaslikH3>Mevcut Rapor Türleri</BaslikH3>

            <AlanTablosu alanlar={[
                { ad: 'Finansal Özet', aciklama: 'Dönem bazlı gelir-gider ve kasa durumu' },
                { ad: 'Tahsilat Performansı', aciklama: 'Personel bazlı tahsilat başarısı ve hedef karşılaştırması' },
                { ad: 'Nakit Akışı', aciklama: 'Proje bazlı gelecek dönem nakit akışı projeksiyonu' },
                { ad: 'Hesap Hareketleri', aciklama: 'Kasa bazlı detaylı hareket dökümü' },
                { ad: 'Envanter Raporu', aciklama: 'Satılmış, kalan ve rezerve dairelerin finansal özeti' },
                { ad: 'Stok Erime Hızı', aciklama: 'Hangi dairelerin ne kadar sürede satıldığı analizi' },
            ]} />

            <Gorsel aciklama="Raporlar ana sayfası — rapor kartları grid düzeninde, her kart rapor adı ve kısa açıklamasıyla" />

            <BaslikH3>Rapor Çalıştırma</BaslikH3>

            <Adim no={1} baslik="Raporu seçin">
                İstediğiniz rapor kartına tıklayın.
            </Adim>

            <Adim no={2} baslik="Filtreleri ayarlayın">
                Tarih aralığı, proje, kasa veya kullanıcı filtrelerini girin.
            </Adim>

            <Adim no={3} baslik="Çalıştırın">
                <strong>Raporu Getir</strong> butonuna tıklayın.
            </Adim>

            <Adim no={4} baslik="Dışa aktarın">
                Raporu <strong>PDF</strong> veya <strong>Excel</strong> formatında indirin.
            </Adim>

            <Gorsel aciklama="Tahsilat Performansı raporu — personel satırları, tahsilat tutarı, hedef ve % başarı sütunları" />
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       İŞ PLANI — Yeni Plan Oluşturma
    ════════════════════════════════════════════════════════════ */
    'is-plani/yeni-plan': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                İş Planı modülü; proje bazlı inşaat süreçleri, görevler ve kilometre taşlarının
                takibini sağlar. Görevler sorumlu kişilere atanır, tamamlanma durumları izlenir.
            </p>

            <BaslikH3>Yeni Plan Oluşturma</BaslikH3>

            <Adim no={1} baslik="İş Planı sayfasını açın">
                Sol menüden <strong>İş Planı</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Plan' butonuna tıklayın">
                Sağ üst köşedeki <strong>+ Yeni Plan</strong> butonuna tıklayın.
            </Adim>

            <Gorsel aciklama="İş Planı sayfası — plan listesi veya boş durum mesajı ve '+ Yeni Plan' butonu" />

            <Adim no={3} baslik="Plan bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Plan Adı', aciklama: 'Planın başlığı (örn. "A Blok İnşaat Süreci")', zorunlu: true },
                    { ad: 'Proje', aciklama: 'Bağlı olduğu proje' },
                    { ad: 'Başlangıç', aciklama: 'Plan başlangıç tarihi' },
                    { ad: 'Bitiş', aciklama: 'Hedeflenen tamamlanma tarihi' },
                    { ad: 'Açıklama', aciklama: 'Plan hakkında ek notlar' },
                ]} />
            </Adim>

            <Adim no={4} baslik="Görev ekleyin">
                Plan oluşturulduktan sonra <strong>+ Görev Ekle</strong> butonuyla alt görevler ekleyin.
                Her görev için sorumlu kullanıcı, başlangıç/bitiş tarihi ve öncelik seviyesi belirlenebilir.
            </Adim>

            <Gorsel aciklama="İş planı detayı — görev listesi Kanban veya tablo görünümünde, her görevde sorumlu avatar ve durum" />

            <BaslikH3>Görev Durumları</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: 'Yapılacak', aciklama: 'Henüz başlanmamış görev' },
                { ad: 'Devam Ediyor', aciklama: 'Üzerinde çalışılan görev' },
                { ad: 'İncelemede', aciklama: 'Tamamlandı, onay bekliyor' },
                { ad: 'Tamamlandı', aciklama: 'Onaylanmış, kapatılmış görev' },
            ]} />

            <Ipucu tur="bilgi">
                Görevler <strong>Ajanda</strong> modülünde de görüntülenir. Sorumlu kullanıcı, görevin
                kendi takviminde göründüğünü fark edecektir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       ARAÇLAR — Fihrist
    ════════════════════════════════════════════════════════════ */
    'araclar/fihrist': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Fihrist modülü; sistemdeki tüm müşteri ve cari kayıtlarına hızlı erişim sağlayan
                alfabetik bir rehberdir. Telefon rehberi gibi çalışır.
            </p>

            <BaslikH3>Fihrist Kullanımı</BaslikH3>

            <Adim no={1} baslik="Fihrist sayfasını açın">
                Sol menüden <strong>Araçlar &rsaquo; Fihrist</strong> bağlantısına tıklayın.
            </Adim>

            <Gorsel aciklama="Fihrist sayfası — soldaki harf seçici, sağda seçili harfle başlayan kişi ve firma listesi" />

            <Adim no={2} baslik="Harf seçin">
                Sol taraftaki alfabetik harf menüsünden bir harfe tıklayın.
                O harfle başlayan tüm müşteri ve cari kayıtları listelenir.
            </Adim>

            <Adim no={3} baslik="Kayda gidin">
                Listeden bir isme tıklayarak doğrudan müşteri veya cari detay sayfasına geçin.
            </Adim>

            <Ipucu tur="bilgi">
                Fihrist; müşteriler, tedarikçiler ve tüm cari kayıtlarını birlikte listeler.
                Türe göre filtrelemek için üst kısımdaki filtre seçeneklerini kullanın.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       ARAÇLAR — Dokümanlar
    ════════════════════════════════════════════════════════════ */
    'araclar/dokumanlar': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Dokümanlar modülü, sisteme yüklenen ve herhangi bir kayda bağlı olmayan
                genel belgelerin merkezi arşividir.
            </p>

            <BaslikH3>Belge Yükleme</BaslikH3>

            <Adim no={1} baslik="Dokümanlar sayfasını açın">
                Sol menüden <strong>Araçlar &rsaquo; Dokümanlar</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yükle' butonuna tıklayın">
                <strong>+ Yükle</strong> butonuna tıklayıp bilgisayarınızdan dosya seçin.
                PDF, Word, Excel, PNG ve JPG formatları desteklenir.
            </Adim>

            <Gorsel aciklama="Dokümanlar sayfası — dosya listesi, her satırda dosya adı, boyut, yüklenme tarihi ve indirme butonu" />

            <Adim no={3} baslik="Kategorilendirin">
                Yüklenen belgeye klasör veya etiket atayarak düzenleyin.
            </Adim>

            <BaslikH3>Belge İndirme ve Silme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Her dosya satırında <strong>İndir</strong> ve <strong>Sil</strong> butonları bulunur.
                Silinen dosyalar geri getirilemez.
            </p>

            <Ipucu tur="bilgi">
                Müşteri, sözleşme veya cari kayıtlarına ait belgeler, bu modül yerine ilgili kaydın
                <strong> Dokümanlar</strong> sekmesine yüklenmesi önerilir; böylece kayıtla ilişki kurulmuş olur.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       ARAÇLAR — Kredi Hesaplama
    ════════════════════════════════════════════════════════════ */
    'araclar/kredi-hesaplama': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Kredi Hesaplama aracı, müşteriye konut kredisi simülasyonu yapmanızı sağlar.
                Farklı faiz oranları ve vade seçenekleri karşılaştırılabilir.
            </p>

            <BaslikH3>Hesaplama Yapma</BaslikH3>

            <Adim no={1} baslik="Kredi Hesaplama sayfasını açın">
                Sol menüden <strong>Araçlar &rsaquo; Kredi Hesaplama</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="Parametreleri girin">
                <AlanTablosu alanlar={[
                    { ad: 'Kredi Tutarı', aciklama: 'Kullanılacak konut kredisi miktarı', zorunlu: true },
                    { ad: 'Faiz Oranı', aciklama: 'Aylık veya yıllık faiz oranı (%)', zorunlu: true },
                    { ad: 'Vade', aciklama: 'Kredi vadesi (ay)', zorunlu: true },
                    { ad: 'Banka', aciklama: 'Tanımlı bankalar listesinden seçim (faiz oranı otomatik dolar)' },
                ]} />
            </Adim>

            <Gorsel aciklama="Kredi hesaplama formu — tutar, faiz, vade giriş alanları ve 'Hesapla' butonu" />

            <Adim no={3} baslik="'Hesapla' butonuna tıklayın">
                <strong>Hesapla</strong> butonuna basın.
                Aylık taksit, toplam ödeme ve toplam faiz miktarı görüntülenir.
            </Adim>

            <Gorsel aciklama="Hesaplama sonucu — aylık taksit büyük rakamla, altında toplam ödeme ve faiz bilgileri, taksit tablosu" />

            <Adim no={4} baslik="Taksit tablosunu inceleyin">
                Her ay için anapara ve faiz dağılımını gösteren ayrıntılı taksit tablosu aşağıda listelenir.
            </Adim>

            <Ipucu tur="bilgi">
                Sistem Ayarları &rsaquo; Banka Kredileri bölümünde bankaların güncel faiz oranları
                tanımlanabilir; bu sayede kredi hesaplamada banka seçimi otomatik oranı getirir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       ARAÇLAR — Hesap Makinesi
    ════════════════════════════════════════════════════════════ */
    'araclar/hesap-makinesi': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Sistem içinde kullanabileceğiniz temel hesap makinesidir.
                Hızlı hesaplama yaparken uygulamadan çıkmadan kullanabilirsiniz.
            </p>

            <Gorsel aciklama="Hesap makinesi arayüzü — rakam tuşları, işlemciler ve sonuç ekranı" />

            <Ipucu tur="bilgi">
                Hesap makinesini klavyeyle de kullanabilirsiniz; rakam ve işlem tuşları aktiftir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       ARAÇLAR — Metin Editörü
    ════════════════════════════════════════════════════════════ */
    'araclar/metin-editoru': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Metin Editörü, zengin metin içeriği oluşturmanızı sağlayan basit bir kelime işlemcidir.
                Şirket içi duyuru, sunum metni veya taslak sözleşme hazırlamak için kullanılabilir.
            </p>

            <BaslikH3>Temel Kullanım</BaslikH3>

            <Adim no={1} baslik="Metin Editörünü açın">
                Sol menüden <strong>Araçlar &rsaquo; Metin Editörü</strong> bağlantısına tıklayın.
            </Adim>

            <Gorsel aciklama="Metin editörü — araç çubuğu (kalın, italik, liste, hizalama vb.) ve geniş yazı alanı" />

            <Adim no={2} baslik="Metni yazın ve biçimlendirin">
                Araç çubuğu aracılığıyla metni biçimlendirebilirsiniz:
                <AlanTablosu alanlar={[
                    { ad: 'Kalın / İtalik', aciklama: 'Metin vurgusu' },
                    { ad: 'Başlıklar', aciklama: 'H1, H2, H3 başlık seviyeleri' },
                    { ad: 'Listeler', aciklama: 'Madde işaretli veya numaralı liste' },
                    { ad: 'Hizalama', aciklama: 'Sol, orta, sağ ve iki yana hizalama' },
                    { ad: 'Tablo', aciklama: 'Basit tablo oluşturma' },
                ]} />
            </Adim>

            <Adim no={3} baslik="Kaydedin veya kopyalayın">
                İçeriği <strong>Kaydet</strong> butonuyla sisteme kaydedebilir
                veya panoya kopyalayarak başka bir yerde kullanabilirsiniz.
            </Adim>

            <Ipucu tur="bilgi">
                Döküman Şablonları bölümündeki şablonların içeriği de bu editörle oluşturulur.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — Kullanıcı Yönetimi
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/kullanici-yonetimi': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Kullanıcı Yönetimi; sisteme erişecek kullanıcı hesaplarının oluşturulmasını,
                rol ve yetki atamalarını, şifre yönetimini kapsar.
            </p>

            <BaslikH3>Yeni Kullanıcı Ekleme</BaslikH3>

            <Adim no={1} baslik="Kullanıcı Yönetimi sayfasını açın">
                <strong>Ayarlar &rsaquo; Kullanıcı Yönetimi</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Kullanıcı' butonuna tıklayın">
                <strong>+ Yeni Kullanıcı</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Kullanıcı bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Ad Soyad', aciklama: 'Kullanıcının tam adı', zorunlu: true },
                    { ad: 'E-posta', aciklama: 'Giriş için kullanılacak e-posta', zorunlu: true },
                    { ad: 'Şifre', aciklama: 'İlk giriş şifresi', zorunlu: true },
                    { ad: 'Rol', aciklama: 'Yönetici, Satış Temsilcisi, Muhasebe vb.', zorunlu: true },
                    { ad: 'Modül Yetkileri', aciklama: 'Hangi modüllere erişebileceği seçilir' },
                    { ad: 'Aktif', aciklama: 'Kullanıcı hesabının aktif/pasif durumu' },
                ]} />
            </Adim>

            <Gorsel aciklama="Yeni kullanıcı formu — ad, e-posta, şifre, rol ve modül yetki checkboxları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <BaslikH3>Şifre Sıfırlama</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Kullanıcı listesindeki ilgili kullanıcıya tıklayıp <strong>Şifre Sıfırla</strong>
                butonuyla yeni şifre belirleyebilirsiniz.
            </p>

            <Ipucu tur="uyari">
                Yönetici rolüne sahip kullanıcılar tüm modüllere ve ayarlara erişebilir.
                Bu rolü yalnızca güvenilir kişilere verin.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — Kurumsal Bilgiler
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/kurumsal-bilgiler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Kurumsal Bilgiler sayfasında firmanızın adı, logosu, iletişim bilgileri ve vergi bilgileri
                tanımlanır. Bu bilgiler sözleşme ve rapor belgelerinde otomatik olarak kullanılır.
            </p>

            <BaslikH3>Bilgileri Düzenleme</BaslikH3>

            <Adim no={1} baslik="Kurumsal Bilgiler sayfasını açın">
                <strong>Ayarlar &rsaquo; Kurumsal Bilgiler</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="Firma Bilgileri sekmesini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Firma Adı', aciklama: 'Resmi ticaret unvanı', zorunlu: true },
                    { ad: 'Logo', aciklama: 'PNG veya JPG formatında firma logosu' },
                    { ad: 'Adres', aciklama: 'Yasal iş adresi' },
                    { ad: 'Telefon', aciklama: 'Ana iletişim telefonu' },
                    { ad: 'E-posta', aciklama: 'Kurumsal e-posta adresi' },
                    { ad: 'Vergi Dairesi', aciklama: 'Bağlı olunan vergi dairesi' },
                    { ad: 'Vergi No', aciklama: 'Vergi kimlik numarası' },
                    { ad: 'Lisans Kodu', aciklama: '64 haneli lisans kodu — salt okunur, değiştirilemez' },
                ]} />
            </Adim>

            <Gorsel aciklama="Firma Bilgileri sekmesi — logo yükleme alanı, firma adı, adres ve vergi alanları" />

            <Adim no={3} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="bilgi">
                Logo yüklendikten sonra sözleşme, sunum belgesi ve raporların başlığında
                firma logonuz otomatik görünür.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — Döküman Şablonları
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/dokuman-sablonlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Döküman Şablonları, sözleşme ve resmi belgelerin Word/PDF olarak otomatik
                üretilmesini sağlayan şablonların yönetildiği bölümdür.
                Şablonlarda <code>&#123;&#123;değişken&#125;&#125;</code> etiketleri kullanılır.
            </p>

            <BaslikH3>Yeni Şablon Oluşturma</BaslikH3>

            <Adim no={1} baslik="Döküman Şablonları sayfasını açın">
                <strong>Ayarlar &rsaquo; Döküman Şablonları</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Şablon' butonuna tıklayın">
                <strong>+ Yeni Şablon</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Şablon bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Şablon Adı', aciklama: '"Satış Sözleşmesi", "Ön Sözleşme" vb.', zorunlu: true },
                    { ad: 'Tür', aciklama: 'Word veya HTML şablon tipi' },
                    { ad: 'Dosya Yükle', aciklama: 'Hazır Word dosyası yüklenebilir' },
                    { ad: 'İçerik', aciklama: 'Metin editörüyle doğrudan içerik yazılabilir' },
                ]} />
            </Adim>

            <BaslikH3>Kullanılabilir Değişkenler</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: '&#123;&#123;musteriAdi&#125;&#125;', aciklama: 'Müşterinin adı soyadı' },
                { ad: '&#123;&#123;musteriTc&#125;&#125;', aciklama: 'TC kimlik numarası' },
                { ad: '&#123;&#123;daireNo&#125;&#125;', aciklama: 'Daire numarası' },
                { ad: '&#123;&#123;projeAdi&#125;&#125;', aciklama: 'Proje adı' },
                { ad: '&#123;&#123;satisFiyati&#125;&#125;', aciklama: 'Toplam satış fiyatı' },
                { ad: '&#123;&#123;sozlesmeTarihi&#125;&#125;', aciklama: 'Sözleşme tarihi' },
                { ad: '&#123;&#123;firmaAdi&#125;&#125;', aciklama: 'Kurumsal Bilgiler bölümündeki firma adı' },
            ]} />

            <Gorsel aciklama="Döküman şablonları listesi — şablon adı, tür ve son güncelleme sütunları" />

            <Ipucu tur="bilgi">
                Şablon dosyanızı Word'de hazırlayıp değişken etiketlerini ekledikten sonra sisteme yükleyin.
                Sözleşme indirme işleminde bu şablon otomatik olarak doldurulur.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — İşlem Kayıtları
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/islem-kayitlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                İşlem Kayıtları (Audit Log), sistemde gerçekleştirilen tüm kritik işlemlerin
                kim tarafından, ne zaman ve ne üzerinde yapıldığını gösteren denetim kaydıdır.
            </p>

            <BaslikH3>İşlem Kayıtlarını Görüntüleme</BaslikH3>

            <Adim no={1} baslik="İşlem Kayıtları sayfasını açın">
                <strong>Ayarlar &rsaquo; İşlem Kayıtları</strong> bağlantısına tıklayın.
            </Adim>

            <Gorsel aciklama="İşlem kayıtları listesi — tarih, kullanıcı, işlem türü, etkilenen kayıt sütunları" />

            <AlanTablosu alanlar={[
                { ad: 'Tarih', aciklama: 'İşlemin gerçekleştiği tarih ve saat' },
                { ad: 'Kullanıcı', aciklama: 'İşlemi yapan kullanıcının adı' },
                { ad: 'İşlem', aciklama: 'Ekleme, Güncelleme, Silme, Giriş vb.' },
                { ad: 'Modül', aciklama: 'İşlemin yapıldığı modül (Müşteriler, Sözleşmeler vb.)' },
                { ad: 'Detay', aciklama: 'Etkilenen kayıt ve değişikliğin özeti' },
            ]} />

            <BaslikH3>Filtreleme</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Tarih aralığı, kullanıcı veya işlem türüne göre filtreleyerek belirli kayıtlara ulaşabilirsiniz.
            </p>

            <Gorsel aciklama="İşlem kayıtları filtre paneli — tarih aralığı, kullanıcı dropdown ve işlem türü seçimi" />

            <Ipucu tur="uyari">
                İşlem kayıtları salt okunurdur; silinemez veya düzenlenemez.
                Bu kayıtlar yalnızca Yönetici rolüne sahip kullanıcılar tarafından görüntülenebilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — Gün Sonu Raporu
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/gun-sonu-raporu': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Gün Sonu Raporu, iş günü sonunda günlük kasa hareketleri, tahsilat özeti ve
                personel aktivitelerini özetleyen bir günlük kapanış raporudur.
            </p>

            <BaslikH3>Gün Sonu Raporu Oluşturma</BaslikH3>

            <Adim no={1} baslik="Gün Sonu Raporu sayfasını açın">
                <strong>Ayarlar &rsaquo; Gün Sonu Raporu</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="Tarih seçin">
                Raporu almak istediğiniz tarihi seçin. Varsayılan olarak bugünün tarihi gelir.
            </Adim>

            <Adim no={3} baslik="'Raporu Getir' butonuna tıklayın">
                <strong>Raporu Getir</strong> butonuna basın.
            </Adim>

            <Gorsel aciklama="Gün sonu raporu — kasa özeti, günlük tahsilat toplamı, yeni kayıt sayıları ve kapatan kullanıcı bilgisi" />

            <AlanTablosu alanlar={[
                { ad: 'Kasa Özeti', aciklama: 'Günlük kasa giriş/çıkış toplamları ve gün sonu bakiye' },
                { ad: 'Tahsilat', aciklama: 'Gün içinde alınan tahsilat toplamı' },
                { ad: 'Ödemeler', aciklama: 'Gün içinde yapılan ödemeler' },
                { ad: 'Yeni Sözleşmeler', aciklama: 'Bugün oluşturulan sözleşme sayısı' },
                { ad: 'Yeni Potansiyeller', aciklama: 'Bugün eklenen potansiyel müşteri sayısı' },
            ]} />

            <Adim no={4} baslik="PDF olarak indirin">
                <strong>PDF İndir</strong> butonuyla raporu kaydedin.
            </Adim>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — Ödeme Planı Şablonları
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/odeme-plan-sablonlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Ödeme Planı Şablonları, tekrar kullanılan ödeme yapılarını (peşin + taksit, 50/50, 12 ay eşit vb.)
                önceden tanımlayarak sözleşmelerde hızlıca uygulamanızı sağlar.
            </p>

            <BaslikH3>Yeni Şablon Oluşturma</BaslikH3>

            <Adim no={1} baslik="Ödeme Planı Şablonları sayfasını açın">
                <strong>Ayarlar &rsaquo; Ödeme Planı Şablonları</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Şablon' butonuna tıklayın">
                <strong>+ Yeni Şablon</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Şablon bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Şablon Adı', aciklama: '"12 Ay Eşit Taksit", "Peşin + 6 Ay" vb.', zorunlu: true },
                    { ad: 'Taksit Sayısı', aciklama: 'Kaç taksit olduğu' },
                    { ad: 'Taksit Aralığı', aciklama: 'Aylık, 3 aylık vb.' },
                    { ad: 'İlk Ödeme', aciklama: 'Kaparo veya peşinat oranı (%)' },
                    { ad: 'Son Ödeme', aciklama: 'Teslimde ödeme gibi özel son taksit' },
                ]} />
            </Adim>

            <Gorsel aciklama="Ödeme planı şablon listesi — şablon adı, taksit sayısı ve kullanım adedi sütunları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="bilgi">
                Sözleşme ödeme planında <strong>Şablondan Yükle</strong> seçildiğinde bu listeden
                şablon seçilir ve taksitler otomatik hesaplanır.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — Etiketler
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/etiketler': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                Etiketler, müşteri ve potansiyel müşteri kayıtlarına renk kodlu etiket atayarak
                segmentasyon yapmanızı sağlar. Etiketler buradan merkezi olarak yönetilir.
            </p>

            <BaslikH3>Yeni Etiket Oluşturma</BaslikH3>

            <Adim no={1} baslik="Etiketler sayfasını açın">
                <strong>Ayarlar &rsaquo; Etiketler</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Etiket' butonuna tıklayın">
                <strong>+ Yeni Etiket</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Etiket bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Etiket Adı', aciklama: '"VIP", "Yatırımcı", "Peşin Ödeme" vb.', zorunlu: true },
                    { ad: 'Renk', aciklama: 'Etiketin chip rengini renk seçiciden belirleyin' },
                ]} />
            </Adim>

            <Gorsel aciklama="Etiketler listesi — etiket adı ve renkli chip önizlemesi, düzenle ve sil butonları" />

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="bilgi">
                Oluşturulan etiketler; Müşteriler, Potansiyel Müşteri ve Sözleşmeler sayfalarında
                filtre olarak da kullanılabilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — SMS Ayarları
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/sms-ayarlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                SMS Ayarları bölümünde, sistemden gönderilecek SMS mesajları için servis sağlayıcı
                bilgileri ve gönderici adı (başlık) tanımlanır.
            </p>

            <BaslikH3>SMS Servis Ayarları</BaslikH3>

            <Adim no={1} baslik="SMS Ayarları sayfasını açın">
                <strong>Ayarlar &rsaquo; SMS Ayarları</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="Servis bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Servis Sağlayıcı', aciklama: 'NetGSM, Turkcell, Twilio vb.', zorunlu: true },
                    { ad: 'API Kullanıcı Adı', aciklama: 'SMS servisinin API kullanıcı adı', zorunlu: true },
                    { ad: 'API Şifresi', aciklama: 'SMS servisinin API şifresi veya token', zorunlu: true },
                    { ad: 'Gönderici Adı', aciklama: 'SMS\'de gözükecek gönderici başlığı (maks. 11 karakter)' },
                ]} />
            </Adim>

            <Gorsel aciklama="SMS ayarları formu — servis sağlayıcı dropdown, API kimlik bilgileri ve gönderici adı alanları" />

            <Adim no={3} baslik="Test SMS gönderin">
                <strong>Test Gönder</strong> butonuyla bir telefon numarasına test mesajı göndererek
                ayarların doğru çalıştığını doğrulayın.
            </Adim>

            <Adim no={4} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <Ipucu tur="uyari">
                API şifrenizi başkalarıyla paylaşmayın. Bu bilgiler şifreli olarak saklanır
                ancak SMS gönderim maliyetleri servis hesabınızdan kesilir.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — SMS Şablonları
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/sms-sablonlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                SMS Şablonları, tekrar kullanılan mesaj metinlerini değişkenli olarak önceden
                tanımlamanızı sağlar. Taksit hatırlatması, karşılama mesajı gibi şablonlar oluşturulabilir.
            </p>

            <BaslikH3>Yeni Şablon Oluşturma</BaslikH3>

            <Adim no={1} baslik="SMS Şablonları sayfasını açın">
                <strong>Ayarlar &rsaquo; SMS Şablonları</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="'+ Yeni Şablon' butonuna tıklayın">
                <strong>+ Yeni Şablon</strong> butonuna tıklayın.
            </Adim>

            <Adim no={3} baslik="Şablon bilgilerini girin">
                <AlanTablosu alanlar={[
                    { ad: 'Şablon Adı', aciklama: '"Taksit Hatırlatma", "Hoş Geldiniz" vb.', zorunlu: true },
                    { ad: 'Mesaj İçeriği', aciklama: 'SMS metni; değişkenler için &#123;&#123;musteriAdi&#125;&#125; vb. kullanın', zorunlu: true },
                ]} />
            </Adim>

            <Gorsel aciklama="SMS şablon oluşturma — şablon adı ve mesaj metin alanı, karakter sayacı" />

            <BaslikH3>Kullanılabilir Değişkenler</BaslikH3>
            <AlanTablosu alanlar={[
                { ad: '&#123;&#123;musteriAdi&#125;&#125;', aciklama: 'Alıcının adı soyadı' },
                { ad: '&#123;&#123;taksitTutari&#125;&#125;', aciklama: 'Taksit tutarı' },
                { ad: '&#123;&#123;vadeTarihi&#125;&#125;', aciklama: 'Taksitin vade tarihi' },
                { ad: '&#123;&#123;firmaAdi&#125;&#125;', aciklama: 'Kurumsal bilgilerden firma adı' },
            ]} />

            <Ipucu tur="bilgi">
                SMS mesajı 160 karakteri aşarsa iki mesaj olarak iletilir ve iki kat ücret kesilir.
                Şablonunuzu 160 karakterin altında tutmaya çalışın.
            </Ipucu>
        </div>
    ),

    /* ════════════════════════════════════════════════════════════
       SİSTEM AYARLARI — İYS Aktarım Ayarları
    ════════════════════════════════════════════════════════════ */
    'sistem-ayarlari/iys-aktarim-ayarlari': (
        <div>
            <p style={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
                İYS (İleti Yönetim Sistemi), ticari elektronik ileti göndermek için zorunlu
                izin kaydı sistemidir. Bu bölümde İYS API bağlantı bilgileri tanımlanır ve
                müşteri izin kayıtları İYS'ye aktarılır.
            </p>

            <BaslikH3>İYS Bağlantı Ayarları</BaslikH3>

            <Adim no={1} baslik="İYS Aktarım Ayarları sayfasını açın">
                <strong>Ayarlar &rsaquo; İYS Aktarım Ayarları</strong> bağlantısına tıklayın.
            </Adim>

            <Adim no={2} baslik="API bilgilerini doldurun">
                <AlanTablosu alanlar={[
                    { ad: 'Marka Kodu', aciklama: 'İYS sisteminde kayıtlı marka kodu', zorunlu: true },
                    { ad: 'API Kullanıcı', aciklama: 'İYS API kullanıcı adı', zorunlu: true },
                    { ad: 'API Şifresi', aciklama: 'İYS API şifresi', zorunlu: true },
                    { ad: 'Hizmet Türü', aciklama: 'SMS, E-posta veya Arama için ayrı tanımlamalar' },
                ]} />
            </Adim>

            <Gorsel aciklama="İYS ayarları formu — marka kodu, API kimlik bilgileri ve hizmet türü checkboxları" />

            <Adim no={3} baslik="Kaydedin">
                <strong>Kaydet</strong> butonuna basın.
            </Adim>

            <BaslikH3>İzin Kaydı Aktarma</BaslikH3>
            <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Müşteri kaydındaki <strong>İYS İzinleri</strong> sekmesinden bireysel aktarım yapılabileceği gibi,
                bu sayfadaki <strong>Toplu Aktar</strong> butonuyla tüm bekleyen izin kayıtları
                İYS sistemine gönderilebilir.
            </p>

            <Gorsel aciklama="İYS aktarım sayfası — bekleyen kayıt sayısı özeti ve 'Toplu Aktar' butonu" />

            <Ipucu tur="uyari">
                İzin alınmadan ticari SMS veya e-posta göndermek İYS mevzuatı kapsamında
                idari para cezasına yol açabilir. Aktarım işlemini düzenli yapın.
            </Ipucu>
        </div>
    ),
};
